import { HUMAN_FACE_NEUTRAL, HUMAN_HEAD_ORIGIN, HUMAN_JAW_ROWS, HUMAN_NECK_BASE_RISE, humanNeck, humanNeckTop, type HumanFace, type HumanProportions, type HumanRigId, type HumanSkull, type SkullBlob } from '../../../content/characters/rigs/human.js';
import { faceAlong, meshField, settle, type FieldMesh } from './field-mesh.js';
import { simplifyMesh } from './simplify-mesh.js';
import { chain, ellipsoid, paired, roundCone, smax, smin, sphere, subtract, torus, union, type Shape } from './sdf.js';
import { fitHair, type HairFit } from './hair-fit.js';
import { INK_MARGIN, humanTattoos } from '../../../content/characters/rigs/human-markings.js';
import { sculptNose } from './nose-sculpt.js';
import type { FaceWarpExtras } from '../../../content/characters/rigs/humanoid-face-shape.js';

/**
 * The head, sculpted as one signed distance field: cranium, brow ridge, sockets and lids, cheekbones, a nose grown
 * from the brow, lips, jaw, chin and ears are smoothly blended surfaces, meshed with the field's own
 * normals. Eyes are separate eyeballs behind lid openings carved through the field; brows, lash lines and lips are
 * thin painted layers laid on the surface. Hair and facial hair are their own closed volumes (see `hairParts` and
 * `beardParts`), so nothing of them hides under the skin. Face shapes are smooth warps of each rig's own face
 * (`faceWarp`), applied alike to the face, its paint and the facial hair, so every variant keeps one topology.
 * Everything here is in skull space: heights as on the reference figure, before the head's scale.
 */
export type HeadMaterial = 'face' | 'ivory' | 'eye' | 'dark' | 'lips' | 'brow' | 'strands' | 'cloth' | 'leather' | 'makeup' | 'liner' | 'accent' | 'lipstick' | 'tattoo';
/**
 * How finely a head is meshed: `high` and `low` simplify it, the Character detail setting (`graphics-options.ts`);
 * at the playing zoom a face is a few dozen pixels across. Close-ups (the character creator, the inventory, the explorer) draw `high`.
 */
export type HeadDetail = 'high' | 'low';
export interface HeadPiece {
  material: HeadMaterial;
  positions: Float32Array; normals: Float32Array; indices: Uint32Array;
  /** Multiplies the material colour per vertex. */
  colors?: Float32Array;
  /** How much of each vertex the torso carries instead of the head: long hair resting on the back. */
  hanging?: Float32Array;
  /** How freely each vertex swings with the body, from 0 (held to the head) to 1 (loose): the free ends of tails, plaits and long hair. */
  sway?: Float32Array;
  /** Texture coordinates, for a piece painted from a texture (face ink). */
  uvs?: Float32Array;
  /** Strands only: the direction the hair runs at each vertex, and how fully it hides the skin, 0 at its edge (see `hair-shading.ts`). */
  flow?: Float32Array; cover?: Float32Array;
  /** Strands only: where each vertex lies among the strands, as a point on a circle round the hair's axis and a height (see `hair-shading.ts`). */
  strandAt?: Float32Array;
}
type Vec = [number, number, number];
type Field = (x: number, y: number, z: number) => number;
/** How hair strands run over a piece (any length; it is laid into the surface) and how fully it covers the skin, 0 at its edge. */
/** The circle strands are laid round, in skull units: about the head's own radius, so strand widths read true over the scalp. */
const STRAND_RADIUS = .1;
interface Strand { flow: (x: number, y: number, z: number) => Vec; cover?: Field;
  /** The upright axis (x, z) the strands fan out round: the crown, a tie, the middle of the chin. Strands are told apart by their angle round it. */
  axis?: [number, number];
  /** How far to stand the covered part out along its normals: a simplified mesh cuts chords under the true surface, and the skin's own chords can then show through. */
  lift?: number }
/** How far a simplified mesh's hair stands out to stay over the skin, by detail. */
const HAIR_LIFT: Record<HeadDetail, number> = { high: .0018, low: .0045 };

const clamp = (v: number, a: number, b: number) => v < a ? a : v > b ? b : v;
const smooth = (v: number, a: number, b: number) => { const t = clamp((v - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };
const mix = (a: number, b: number, t: number) => a + (b - a) * t;
/**
 * A layer grown out of the skin (`d` is the skin's field) within a region (`inside`, negative within): from just under
 * the skin to `thickness` above it. Over `feather` inside the region's edge it sinks back under the skin, so its edge
 * is a soft line where it meets the skin rather than a cut face.
 */
function layer(d: number, thickness: number, inside: number, feather: number) {
  const rise = smooth(-inside, 0, feather);
  return Math.max(d - thickness * rise + .001 * (1 - rise), -(d + .0045), inside);
}
/**
 * Where a layer rises out of the skin (`rise` is 0 at its edge and 1 where it is full), its surface is shaded like the
 * skin beneath, so the edge reads as hair lying on the skin rather than as a dark ledge. Only points near the skin
 * change, so a tail or a hanging beard keeps its own shading.
 */
function softenEdge(mesh: FieldMesh, skin: Field, rise: Field) {
  const { positions, normals } = mesh;
  for (let i = 0; i < positions.length / 3; i++) {
    const x = positions[i * 3], y = positions[i * 3 + 1], z = positions[i * 3 + 2];
    const w = (1 - rise(x, y, z)) * (1 - smooth(skin(x, y, z), .002, .005));
    if (w <= 0) continue;
    const g = gradient(skin, x, y, z), nx = mix(normals[i * 3], g[0], w), ny = mix(normals[i * 3 + 1], g[1], w), nz = mix(normals[i * 3 + 2], g[2], w), l = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
    normals[i * 3] = nx / l; normals[i * 3 + 1] = ny / l; normals[i * 3 + 2] = nz / l;
  }
}
/** Eases a mesh's open edge along itself, `rounds` times, so a cut that follows grid cells becomes a smooth line. */
function relaxEdge(mesh: FieldMesh, rounds: number) {
  const { positions, indices } = mesh, uses = new Map<number, number>(), key = (a: number, b: number) => a < b ? a * 4294967296 + b : b * 4294967296 + a;
  for (let t = 0; t < indices.length; t += 3) for (let k = 0; k < 3; k++) {
    const edge = key(indices[t + k], indices[t + (k + 1) % 3]); uses.set(edge, (uses.get(edge) ?? 0) + 1);
  }
  const neighbours = new Map<number, number[]>();
  for (const [edge, count] of uses) {
    if (count !== 1) continue;
    const a = Math.floor(edge / 4294967296), b = edge - a * 4294967296;
    (neighbours.get(a) ?? neighbours.set(a, []).get(a)!).push(b); (neighbours.get(b) ?? neighbours.set(b, []).get(b)!).push(a);
  }
  const rim = [...neighbours].filter(([, next]) => next.length === 2), moved = new Float32Array(rim.length * 3);
  for (let round = 0; round < rounds; round++) {
    rim.forEach(([vertex, [a, b]], i) => { for (let c = 0; c < 3; c++) moved[i * 3 + c] = positions[vertex * 3 + c] * .5 + (positions[a * 3 + c] + positions[b * 3 + c]) * .25; });
    rim.forEach(([vertex], i) => { for (let c = 0; c < 3; c++) positions[vertex * 3 + c] = moved[i * 3 + c]; });
  }
}
/** Pulls a colour toward `to` by `amount` (0 to 1). */
function shade(out: number[], amount: number, to: readonly number[]) { for (let c = 0; c < 3; c++) out[c] *= 1 - amount * (1 - to[c]); }

function gradient(f: Field, x: number, y: number, z: number, e = 2e-4): Vec {
  const gx = f(x + e, y, z) - f(x - e, y, z), gy = f(x, y + e, z) - f(x, y - e, z), gz = f(x, y, z + e) - f(x, y, z - e), l = Math.sqrt(gx * gx + gy * gy + gz * gz) || 1;
  return [gx / l, gy / l, gz / l];
}
/** Where a line looking back along -z from the front first meets the surface, at height y and side offset x. */
function front(f: Field, x: number, y: number, from = .16): number | undefined {
  let z = from;
  for (let i = 0; i < 400 && z > -.15; i++) {
    const d = f(x, y, z);
    if (d < 2e-6) {
      for (let n = 0; n < 3; n++) { const dz = (f(x, y, z + 1e-4) - f(x, y, z - 1e-4)) / 2e-4; if (Math.abs(dz) > .05) z -= f(x, y, z) / dz; }
      return z;
    }
    z -= Math.max(d * .8, 1e-5);
  }
  return undefined;
}

/** Wraps a field mesh as a piece, colouring each vertex by `tint`. */
function fieldPiece(material: HeadMaterial, mesh: FieldMesh, tint?: (x: number, y: number, z: number, out: number[]) => void, hang?: (x: number, y: number, z: number) => number, sway?: (x: number, y: number, z: number) => number, strand?: Strand): HeadPiece {
  const count = mesh.positions.length / 3, piece: HeadPiece = { material, positions: mesh.positions, normals: mesh.normals, indices: mesh.indices };
  if (strand) {
    // The flow lies in the surface: what of it points out of the hair is taken away.
    const flow = new Float32Array(count * 3), cover = new Float32Array(count), at = new Float32Array(count * 3), [ax, az] = strand.axis ?? [0, -.04];
    for (let i = 0; i < count; i++) {
      const x = mesh.positions[i * 3], y = mesh.positions[i * 3 + 1], z = mesh.positions[i * 3 + 2], [fx, fy, fz] = strand.flow(x, y, z);
      const nx = mesh.normals[i * 3], ny = mesh.normals[i * 3 + 1], nz = mesh.normals[i * 3 + 2], out = fx * nx + fy * ny + fz * nz;
      let tx = fx - nx * out, ty = fy - ny * out, tz = fz - nz * out, l = Math.hypot(tx, ty, tz);
      if (l < 1e-6) { tx = 0; ty = -1; tz = 0; l = 1; }
      flow[i * 3] = tx / l; flow[i * 3 + 1] = ty / l; flow[i * 3 + 2] = tz / l;
      cover[i] = strand.cover ? clamp(strand.cover(x, y, z), 0, 1) : 1;
      // A point on a circle for the angle round the axis, so the strands have no seam where the angle wraps, and the height along them.
      const angle = Math.atan2(x - ax, z - az);
      at[i * 3] = Math.sin(angle) * STRAND_RADIUS; at[i * 3 + 1] = Math.cos(angle) * STRAND_RADIUS; at[i * 3 + 2] = y;
    }
    piece.flow = flow; piece.cover = cover; piece.strandAt = at;
    if (strand.lift) for (let i = 0; i < count; i++) for (let k = 0; k < 3; k++) mesh.positions[i * 3 + k] += mesh.normals[i * 3 + k] * strand.lift * cover[i];
  }
  if (tint) {
    const colors = new Float32Array(count * 3), out = [1, 1, 1];
    for (let i = 0; i < count; i++) { out[0] = out[1] = out[2] = 1; tint(mesh.positions[i * 3], mesh.positions[i * 3 + 1], mesh.positions[i * 3 + 2], out); colors.set(out, i * 3); }
    piece.colors = colors;
  }
  if (hang) piece.hanging = Float32Array.from({ length: count }, (_, i) => hang(mesh.positions[i * 3], mesh.positions[i * 3 + 1], mesh.positions[i * 3 + 2]));
  if (sway) piece.sway = Float32Array.from({ length: count }, (_, i) => sway(mesh.positions[i * 3], mesh.positions[i * 3 + 1], mesh.positions[i * 3 + 2]));
  return piece;
}
/**
 * A surface from rows of points with their normals; each triangle is wound to face along the normals, so any
 * row order works. Optional colours per point.
 */
function gridPiece(material: HeadMaterial, points: Vec[][], normals: Vec[][], colors?: Vec[][]): HeadPiece {
  const rows = points.length, columns = points[0].length, positions: number[] = [], outNormals: number[] = [], outColors: number[] = [], indices: number[] = [];
  for (let r = 0; r < rows; r++) for (let c = 0; c < columns; c++) { positions.push(...points[r][c]); outNormals.push(...normals[r][c]); if (colors) outColors.push(...colors[r][c]); }
  const at = (index: number) => [positions[index * 3], positions[index * 3 + 1], positions[index * 3 + 2]];
  const triangle = (a: number, b: number, c: number) => {
    const [ax, ay, az] = at(a), [bx, by, bz] = at(b), [cx, cy, cz] = at(c);
    const nx = (by - ay) * (cz - az) - (bz - az) * (cy - ay), ny = (bz - az) * (cx - ax) - (bx - ax) * (cz - az), nz = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
    const mx = outNormals[a * 3] + outNormals[b * 3] + outNormals[c * 3], my = outNormals[a * 3 + 1] + outNormals[b * 3 + 1] + outNormals[c * 3 + 1], mz = outNormals[a * 3 + 2] + outNormals[b * 3 + 2] + outNormals[c * 3 + 2];
    if (nx * nx + ny * ny + nz * nz < 1e-20) return;
    if (nx * mx + ny * my + nz * mz >= 0) indices.push(a, b, c); else indices.push(a, c, b);
  };
  for (let r = 0; r < rows - 1; r++) for (let c = 0; c < columns - 1; c++) {
    const a = r * columns + c, b = a + 1, d = a + columns, e = d + 1;
    triangle(a, d, b); triangle(b, d, e);
  }
  return { material, positions: new Float32Array(positions), normals: new Float32Array(outNormals), indices: new Uint32Array(indices), colors: colors ? new Float32Array(outColors) : undefined };
}

export interface Eye { x: number; y: number; z: number; r: number; w: number; up: number; down: number; tilt: number }
/** The lid opening of the right eye in its own frame: u outward from the centre, v up from it. */
function lidLine(e: Eye) {
  const RU = (e.w ** 2 + e.up ** 2) / (2 * e.up), RL = (e.w ** 2 + e.down ** 2) / (2 * e.down), cu = e.up - RU, cl = RL - e.down;
  // The upper lid peaks toward the nose, the lower lid dips toward the outer corner.
  const skewUp = (u: number) => u + .22 * e.w * (1 - (u / e.w) ** 2), skewDown = (u: number) => u - .15 * e.w * (1 - (u / e.w) ** 2);
  return {
    /** Negative inside the opening. */
    distance(u: number, v: number) {
      const su = skewUp(u), sd = skewDown(u);
      return Math.max(Math.sqrt(su * su + (v - cu) ** 2) - RU, Math.sqrt(sd * sd + (v - cl) ** 2) - RL);
    },
    upper: (u: number) => cu + Math.sqrt(Math.max(0, RU * RU - skewUp(u) ** 2)),
    lower: (u: number) => cl - Math.sqrt(Math.max(0, RL * RL - skewDown(u) ** 2)),
  };
}
/**
 * The opening of the right eye's lids: a prism in the shape of the opening, in front of the eye's centre, that stops
 * just inside the eyeball, so carving it bares the eyeball and nothing behind it.
 */
function aperture(e: Eye): Shape {
  const line = lidLine(e), inner = e.r - .0004;
  return { cx: e.x, cy: e.y, cz: e.z + e.r, r: e.w + .012, field(x, y, z) {
    const u = x - e.x, v = y - e.y - e.tilt * u, dx = x - e.x, dy = y - e.y, dz = z - e.z;
    return Math.max(line.distance(u, v), e.z - z, inner - Math.sqrt(dx * dx + dy * dy + dz * dz));
  } };
}
/**
 * `base` with its surface pushed out by `relief` (in skull units, negative to press in): broad, gentle forms such as
 * the brow ridge, cheekbones and smile lines, which would read as lumps if built from solids.
 */
function relieved(base: Shape, relief: (x: number, y: number, z: number) => number, most: number): Shape {
  return { cx: base.cx, cy: base.cy, cz: base.cz, r: base.r + most, field: (x, y, z) => base.field(x, y, z) - relief(x, y, z) };
}
const gauss = (d: number, width: number) => Math.exp(-((d / width) ** 2));
/**
 * The face with its jaws moved forward (positive) or back along z, split at the mouth line: the upper jaw up to under the nose, the lower
 * one down through the chin. Each fades out toward the angles of the jaw and behind the face, so the cheeks and neck stay put.
 */
function shiftedJaws(face: Shape, mouthY: number, line: (x: number) => number, upper: number, lower: number): Shape {
  const ease = (a: number, b: number, v: number) => { const t = clamp((v - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };
  return { ...face, r: face.r + Math.max(Math.abs(upper), Math.abs(lower)), field(x, y, z) {
    const ax = Math.abs(x), at = y - line(clamp(x, -.04, .04)), across = 1 - ease(.03, .062, ax), front = ease(-.01, .045, z);
    const below = ease(.003, -.007, at) * ease(1.715, 1.745, y), above = ease(-.003, .007, at) * (1 - ease(.022, .036, y - mouthY));
    const shift = (upper * above + lower * below) * across * front;
    return face.field(x, y, z - shift);
  } };
}
/** Continuous cross-sections through the jaw and cheeks, with a smooth tangent at every row. */
function faceEnvelope(f: HumanFace, sk: HumanSkull): Shape {
  // Height, half width, front depth, centre depth, back depth. The mandibular body carries the cheeks into the chin;
  // separate jaw, chin and mouth solids would leave shelves and inflated patches where they overlap.
  // The lower front sections lean forward, while the deeper rear sections carry the jaw up into the skull base.
  const rows = HUMAN_JAW_ROWS.map((y, i) => [y, sk.jaw.width[i], sk.jaw.front[i], sk.jaw.centre[i], sk.jaw.back[i]]);
  // The same spline is sampled millions of times while meshing. Precompute its polynomials,
  // and find the row once per sample rather than once per width/depth/centre measure.
  const segments = rows.slice(1).map((b, index) => {
    const a = rows[index], before = rows[Math.max(0, index - 1)], after = rows[Math.min(rows.length - 1, index + 2)];
    const span = b[0] - a[0];
    return [1, 2, 3, 4].map(c => {
      const m0 = (b[c] - before[c]) / (b[0] - before[0]) * span, m1 = (after[c] - a[c]) / (after[0] - a[0]) * span;
      return [a[c], m0, -3 * a[c] - 2 * m0 + 3 * b[c] - m1, 2 * a[c] + m0 - 2 * b[c] + m1];
    });
  });
  const evaluate = (c: number[], t: number) => ((c[3] * t + c[2]) * t + c[1]) * t + c[0];
  return { cx: 0, cy: 1.833, cz: .025, r: .13, field(x, y, z) {
    let row = 1; while (row < rows.length - 1 && y > rows[row][0]) row++;
    const t = clamp((y - rows[row - 1][0]) / (rows[row][0] - rows[row - 1][0]), 0, 1), curves = segments[row - 1];
    const jaw = 1 - smooth(y, 1.785, 1.842), chin = 1 - smooth(y, 1.754, 1.783);
    const rx = evaluate(curves[0], t) * mix(1, .94 + .06 * f.jaw, jaw) * mix(1, .86 + .14 * f.chin, chin);
    const cz = evaluate(curves[2], t) + f.chinDepth * chin, frontDepth = evaluate(curves[1], t), backDepth = evaluate(curves[3], t);
    const rz = z >= cz ? frontDepth : backDepth;
    const power = 2 + f.square * .45 * jaw;
    const side = ((Math.abs(x / rx) ** power + Math.abs((z - cz) / rz) ** power) ** (1 / power) - 1) * Math.min(rx, frontDepth, backDepth);
    return smax(side, Math.max(1.744 - y, y - 1.92), .009);
  } };
}

/**
 * The head's own neck, following the body's neck (`humanNeck`) in skull space: a little inside it at the base of
 * the neck and a little outside it where the body's neck ends, so either one hides the other's edge.
 */
function neck(p: HumanProportions): Shape {
  const sections = humanNeck(p), hs = p.headScale, base = sections[0][0];
  /** The sections' measure `c` at a height, on the same smooth curve through them as the body's neck. */
  const along = (rootY: number, c: number) => {
    let i = 1; while (i < sections.length - 1 && rootY > sections[i][0]) i++;
    const p1 = sections[i - 1][c], p2 = sections[i][c], p0 = sections[Math.max(0, i - 2)][c], p3 = sections[Math.min(sections.length - 1, i + 1)][c];
    const t = clamp((rootY - sections[i - 1][0]) / (sections[i][0] - sections[i - 1][0]), 0, 1);
    return .5 * (2 * p1 + (p2 - p0) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t * t + (3 * p1 - p0 - 3 * p2 + p3) * t * t * t);
  };
  return { cx: 0, cy: 1.74, cz: -.01, r: .13, field(x, y, z) {
    const rootY = p.headY + (y - HUMAN_HEAD_ORIGIN) * hs, grow = mix(.94, 1.002, smooth(rootY, base + .003, humanNeckTop(p)));
    const rx = along(rootY, 1) * grow / hs, rz = along(rootY, 2) * grow / hs, cz = (along(rootY, 3) - p.headZ) / hs;
    const side = (Math.sqrt((x / rx) ** 2 + ((z - cz) / rz) ** 2) - 1) * Math.min(rx, rz);
    // It runs on up inside the skull, well clear of the skin, so its end leaves no mark.
    // Keep the lower cap inside the body's taper instead of exposing a cylindrical lip over the chest.
    return Math.max(side, (base - .003 - rootY) / hs, (rootY - p.headY - .155) / hs);
  } };
}

/** The mouth as the sculpt and the lips' paint share it: height, half width, overall fullness and the lip shape knobs (`HumanSkull.mouth`). */
type Mouth = HumanSkull['mouth'] & { w: number; lips: number };
/** Where the lips lie: the mouth line, and the height of each lip above and below it, across the mouth. */
function lipShape(m: Mouth) {
  const w = m.w;
  const across = (x: number) => Math.min(1, Math.abs(x / w));
  return {
    line: (x: number) => m.y + m.corners * (x / w) ** 2,
    // Upper lip: highest at the two peaks of the bow, dipping between them and closing at the corners.
    upper: (x: number) => { const a = across(x); return (.0074 + m.bow * (.0012 * Math.exp(-(((a - .3) / .17) ** 2)) - .0009 * Math.exp(-((a / .09) ** 2)))) * (1 - a ** 2.4) ** .9 * m.lips * m.upper; },
    lower: (x: number) => .0095 * (1 - across(x) ** 2.2) ** .9 * m.lips * m.lower,
  };
}
interface Sculpt { head: Shape; bare: Shape; hairSkin: Shape; headwear: Shape; scalp: Shape; eye: Eye; mouth: Mouth; tip: Vec; noseBase: number; nostril: Shape; concha: Shape; female: boolean; face: HumanFace }
const sculpts = new Map<string, Sculpt>();
// Shape edits get their own cache entries; changing a warp or colour can reuse the sculpt.
export function headSurfaceKey(p: HumanProportions) {
  const { width: _width, length: _length, noseSize: _noseSize, eye: _eye, ...sculptedFace } = p.face;
  const { eyebrows: _eyebrows, ...skull } = p.skull;
  return `${p.headCacheKey ?? p.id}:${JSON.stringify([sculptedFace, skull, humanNeck(p), humanNeckTop(p), p.headScale, p.headY, p.headMeshCell])}`;
}
const headKey = headSurfaceKey;
/** Forgets every built head, hairstyle and beard, so the next build reads the rig's current face and skull (used by the explorer's tuning studies). */
export function resetHeadCaches() { sculpts.clear(); pieces.clear(); stocked.clear(); faceViews.clear(); }
const blob = (b: SkullBlob) => ellipsoid([0, b.y, b.z], [b.w, b.h, b.d]);
/** How much thinner a pointed ear's tip is across the ear than along it. */
const EAR_FLATTEN = 2.4;
/** Presses a shape `factor` times thinner along the side direction (+x) square to the line from `from` to `to`, about that line. */
function flattenedAcross(shape: Shape, from: Vec, to: Vec, factor: number): Shape {
  const length = Math.hypot(to[0] - from[0], to[1] - from[1], to[2] - from[2]), d = [(to[0] - from[0]) / length, (to[1] - from[1]) / length, (to[2] - from[2]) / length];
  const n0 = [1 - d[0] * d[0], -d[0] * d[1], -d[0] * d[2]], nl = Math.hypot(n0[0], n0[1], n0[2]), n = [n0[0] / nl, n0[1] / nl, n0[2] / nl];
  return { ...shape, field(x, y, z) {
    const across = ((x - from[0]) * n[0] + (y - from[1]) * n[1] + (z - from[2]) * n[2]) * (factor - 1);
    return shape.field(x + across * n[0], y + across * n[1], z + across * n[2]) / factor;
  } };
}

/**
 * The rig's own head and face, proportioned on a real head (one skull unit is a little under a metre): about 24 cm
 * from chin to crown with the eyes halfway, 16 cm across the skull and 14 cm across the cheekbones, the angles of the
 * jaw about 12 cm apart, the eyes 6.3 cm apart and the mouth about 4.5 cm wide.
 */
function sculpt(p: HumanProportions): Sculpt {
  const known = sculpts.get(headKey(p));
  if (known) return known;
  // Cached field closures must not observe later edits to the explorer's live rig.
  p = structuredClone(p);
  const f = p.face, sk = p.skull, female = p.motionRig === 'human-female', es = f.eyeSize;
  // A broad, round dome, longer front to back than across, with a forehead in front and the occiput behind.
  const cranium = blob(sk.cranium);
  // The forehead is broad and gently curved across, turning back only at the temples.
  const forehead = blob(sk.forehead);
  // The parietal bosses broaden the top of the skull, so it is not an egg.
  // The broad cranial base carries the jaw's rear corners into the occiput instead of leaving them beside a narrow nape.
  const scalp = union(sk.blend, cranium, forehead, blob(sk.occiput), blob(sk.base), blob(sk.parietal));
  // Both bodies share the orbital plane: larger eyes grow backwards rather than pushing the lids out of the face.
  const eye: Eye = { x: sk.eye.x, y: sk.eye.y, z: sk.eye.plane - .0128 * es, r: .0128 * es, w: .0124 * es, up: .0044 * es * (sk.eye.opening ?? 1), down: .0047 * es * (sk.eye.opening ?? 1), tilt: f.eyeTilt };
  const mouth: Mouth = { ...sk.mouth, w: sk.mouth.width * Math.sqrt(f.jaw), lips: f.lips }, lipLine = lipShape(mouth);
  const lower = faceEnvelope(f, sk);
  // Its underside is cut along the jaw line, which rises from the chin back to the angles of the jaw; under the chin
  // it runs back level to the throat.
  const jawLine = (x: number, z: number) => mix(1.747, mix(1.769, 1.746, smooth(z, -.03, .09)), smooth(Math.abs(x), .02, .055));
  let face: Shape = { ...lower, field: (x: number, y: number, z: number) => smax(lower.field(x, y, z), jawLine(x, z) - y, .014) };
  // A nose drawn along its profile is seated on this bare face.
  // The jaws come forward or sit back from the mouth line: the upper one up to under the nose, the lower one down through the chin.
  if (sk.mouth.upperJaw || sk.mouth.lowerJaw) face = shiftedJaws(face, sk.mouth.y, lipLine.line, sk.mouth.upperJaw ?? 0, sk.mouth.lowerJaw ?? 0);
  const bare = union(.02, scalp, face), { shape: nose, tip, nostril, base: noseBase } = sculptNose(f, sk.nose, (x, y) => front(bare.field, x, y) ?? sk.nose.tipZ - .03);
  // A broad, shallow lid envelope meets the surrounding orbital plane without a spherical roll under the eye.
  // Its centre sits behind the eyeball, leaving only a thin covering at the opening.
  const lid = paired(ellipsoid([eye.x, eye.y, eye.z - .003], [eye.w * 1.5, eye.r * 1.55, eye.r + .0035]));
  // Ear: a tilted shell with the bowl of the concha pressed into it and a lobe below.
  const earTurn: Vec = [-.18, -.32, 0], earAt: Vec = [sk.ear.x, sk.ear.y, sk.ear.z], kt = sk.ear.thickness / .0059, kl = sk.ear.length / .0255, kw = sk.ear.width / .0168;
  const concha = paired(ellipsoid([earAt[0] + .0044 * kt, earAt[1] - .002 * kl, earAt[2] + .0012 * kw], [.0048 * kt, .0136 * kl, .0089 * kw], earTurn));
  const shell = ellipsoid(earAt, [sk.ear.thickness, sk.ear.length, sk.ear.width], earTurn);
  // A pointed ear draws its rim up into a tip: a cone pressed flat across the ear, so it reads as a leaf of cartilage rather than a horn.
  const earTip: Vec = [earAt[0] + (sk.ear.pointOutward ?? .03), earAt[1] + (sk.ear.pointLength ?? .048), earAt[2] - (sk.ear.pointBack ?? .005)];
  const pointed = sk.ear.pointed ? union(.006, shell, flattenedAcross(roundCone(earAt, earTip, .016 * kl, .0012), earAt, earTip, EAR_FLATTEN)) : shell;
  const ear = union(.003, subtract(paired(pointed), concha, .0022), paired(ellipsoid([earAt[0] - .0004 * kt, earAt[1] - .0215 * kl, earAt[2] + .0055 * kw], [.0052 * kt, .0073 * kl, .0066 * kw])));

  // Gentle forms on the face: the brow ridge arching over the eyes, cheekbones with a shallow hollow beneath, the
  // apples of the cheeks, soft temples and the hollow under the lower lip.
  const relief = (x: number, y: number, z: number) => {
    if (y < 1.74 || y > 1.94 || z < -.02) return 0;
    const ax = Math.abs(x), front = smooth(z, .03, .07);
    const ridge = (.0009 + f.brow * .8) * gauss(y - 1.8798 + .0016 * (ax / .05) ** 2, .0074) * (1 - smooth(ax, .044, .064));
    const cheekbone = (.0005 + f.cheek * .2) * Math.exp(-(((ax - .051) / .025) ** 2) - ((y - 1.838) / .022) ** 2);
    const fullness = f.fullness * .45 * Math.exp(-(((ax - .042) / .026) ** 2) - ((y - 1.823) / .025) ** 2);
    // A shallow hollow under the cheekbones, deeper on a lean face.
    const hollow = -(.0002 + f.hollow * .15) * Math.exp(-(((ax - .058) / .024) ** 2) - ((y - 1.802) / .024) ** 2);
    // Lips rise gently out of the continuous oral plane; separate ellipsoids read as a projecting muzzle.
    // Each lip is a pillow between the mouth line and its own edge, with a crease along the line closing at the corners.
    const reach = Math.abs(x) / mouth.w, dy = y - lipLine.line(x), lipHeight = dy > 0 ? lipLine.upper(x) : lipLine.lower(x), t = Math.abs(dy) / Math.max(lipHeight, 1e-5);
    // Both lips taper to a point at the corners, where they lie flush with the skin of the cheek.
    // Each rises steeply from the mouth line, is fullest a third of the way out and eases into the skin past its painted border (the lower
    // lip further, into the chin), so it neither ends on a ledge nor casts a hard shadow under itself.
    const fade = dy > 0 ? 1.35 : 1.8, swell = t < .35 ? Math.sqrt(t / .35) : 1 - smooth(t, .35, fade);
    const pillow = reach < 1 && t < fade ? swell * (dy > 0 ? .0027 : .0034) * mouth.forward * smooth(1 - reach, 0, .4) : 0;
    const crease = -mouth.groove * gauss(dy, .0007) * (1 - Math.min(1, reach) ** 4);
    // The corners tuck into a small hollow, so the lips sit in the face rather than on it.
    const corner = -.0007 * gauss(ax - mouth.w * 1.04, .0035) * gauss(dy, .004);
    const upperLip = pillow + corner, lowerLip = crease;
    const philtrum = (.00035 * gauss(ax - .0043, .0025) - .0002 * gauss(x, .002))
      * gauss(y - (mouth.y + .011), .007);
    // The hollow under the lower lip, rounding into the chin.
    const underLip = -.0006 * gauss(y - (mouth.y - .015) - ax * ax * 3, .006) * gauss(ax, .021);
    // The fold from the nose wing down to past the mouth corner: a groove with the cheek rounding over it on the outside.
    let fold = 0;
    if (f.fold > 0) {
      const wx = .017 * f.nose, wy = noseBase, cx = mouth.w + .006, cy = mouth.y - .008, lx = cx - wx, ly = cy - wy, l2 = lx * lx + ly * ly;
      const t = clamp(((ax - wx) * lx + (y - wy) * ly) / l2, 0, 1), side = ((ax - wx) * ly - (y - wy) * lx) / Math.sqrt(l2);
      const along = Math.hypot(ax - wx - t * lx, y - wy - t * ly) * Math.sign(side || 1), fade = Math.sin(Math.PI * t) ** .6;
      fold = f.fold * fade * (-gauss(along, .0026) + .8 * gauss(along + .0055, .0055));
    }
    const temple = -.0022 * Math.exp(-((ax - .071) ** 2 + ((y - 1.882) / 1.6) ** 2 + ((z - .036) / 1.5) ** 2) / .013 ** 2);
    return (ridge + cheekbone + fullness + hollow + upperLip + lowerLip + philtrum + underLip + fold) * front + temple;
  };
  let head = relieved(union(.02, scalp, face), relief, .008);
  head = union(.008, head, nose);
  head = subtract(head, nostril, .0012);
  head = union(.0035, head, lid);
  head = subtract(head, paired(aperture(eye)), .0011);
  const hairSkin = head; // Long ears carry skin, never the scalp's hair shell.
  head = union(.004, head, ear);
  // The neck meets the underside of the jaw in a soft angle, and blends broadly into the cranial base at the back.
  const skull = head, nape = neck(p);
  head = { ...skull, field(x, y, z) { return smin(skull.field(x, y, z), nape.field(x, y, z), mix(.015, .012, smooth(z, -.05, .01))); } };
  const hairSurface = { ...hairSkin, field(x: number, y: number, z: number) { return smin(hairSkin.field(x, y, z), nape.field(x, y, z), mix(.015, .012, smooth(z, -.05, .01))); } };
  const result: Sculpt = { head, bare, hairSkin: hairSurface, headwear: hairSkin, scalp, eye, mouth, tip, noseBase, nostril, concha, female, face: f };
  if (sculpts.size >= 32) sculpts.delete(sculpts.keys().next().value!);
  sculpts.set(headKey(p), result);
  return result;
}

/** A lower-lip anchor on the actual sculpted surface, used by ancestry-owned teeth and mouth details: `down` is how far below the mouth line, as a share of the lower lip. */
export function lowerMouthSurface(p: HumanProportions, fraction: number, down = .9): { x: number; y: number; z: number } {
  const s = sculpt(p), line = lipShape(s.mouth), x = s.mouth.w * fraction;
  const y = line.line(x) - line.lower(x) * down, z = mouthSkin(s)(x, y)?.point[2];
  if (z === undefined) throw new Error(`No lower mouth surface on '${p.id}' at ${fraction}.`);
  return { x, y, z };
}
/** Signed distance to the sculpted head: negative inside, positive outside. */
export function headSurfaceDistance(p: HumanProportions, point: { x: number; y: number; z: number }): number {
  return sculpt(p).head.field(point.x, point.y, point.z);
}

/** Headwear follows the head's continuous envelope; projecting ears and the body's neck do not stretch a hat. */
export function headwearSurface(p: HumanProportions, face: HumanFace) {
  const surface = sculpt(p).headwear.field, warp = faceWarp(p, face), moved = [0, 0, 0];
  return (x: number, y: number, z: number) => {
    let sx = x, sy = y, sz = z;
    for (let i = 0; i < 4; i++) { warp(sx, sy, sz, moved); sx = x - moved[0]; sy = y - moved[1]; sz = z - moved[2]; }
    return surface(sx, sy, sz);
  };
}

/**
 * How finely each surface is meshed: from a grid of `cell` (no finer than `REDUCED_CELL`), simplified to `reduced` vertices, which keeps
 * them where the surface turns (lids, lips, nostrils, ears) and sheds them where it is flat.
 */
interface Quality { cell: number; reduced: Record<HeadDetail, number> }
/** Longest edge a reduced mesh may keep, so a face shape's warp still bends it rather than its flat stretches staying straight. */
const REDUCED_LONGEST = { high: .016, low: .04 };
/** The grid a reduced mesh starts from: no finer than this, so a head sculpted on a fine grid (goblin, orc, elf, dwarf) simplifies quickly. */
const REDUCED_CELL = { high: .005, low: .0085 };
const FACE: Quality = { cell: .0075, reduced: { high: 2600, low: 700 } };
function surface(field: Field, min: Vec, max: Vec, detail: HeadDetail, quality: Quality, keep?: (x: number, y: number, z: number) => boolean): FieldMesh {
  // Simplified where it is flat, then pulled back onto the surface for its normals.
  const cell = Math.max(quality.cell, REDUCED_CELL[detail]), coarse = meshField(field, { min, max, cell, steps: 0, keep });
  if (coarse.positions.length / 3 <= quality.reduced[detail]) return coarse;
  const simple = simplifyMesh(coarse.positions, coarse.indices, quality.reduced[detail], REDUCED_LONGEST[detail]), normals = settle(field, simple.positions, cell, 1);
  faceAlong(simple.positions, normals, simple.indices);
  return { positions: simple.positions, normals, indices: simple.indices };
}
const pieces = new Map<string, HeadPiece[]>();
export type HeadCacheEntries = [string, HeadPiece[]][];
/** Worker-produced surfaces are identical to locally built ones and remain read-only. */
export function readHeadCache(): HeadCacheEntries { return [...pieces]; }
export function hasHeadCache(key: string) { return pieces.has(key) || stocked.has(key); }
export function installHeadCache(entries: HeadCacheEntries) {
  for (const [key, value] of entries) {
    if (!pieces.has(key) && pieces.size >= 96) pieces.delete(pieces.keys().next().value!);
    pieces.set(key, value);
  }
}
/**
 * Surfaces meshed ahead for figures about to be built (`head-pool.ts`), often more than the cache keeps: they stay, for every figure that
 * shares one, until the figures are built and `dropHeadSurfaces` forgets them.
 */
const stocked = new Map<string, HeadPiece[]>();
export function stockHeadSurfaces(entries: HeadCacheEntries) { for (const [key, value] of entries) if (!pieces.has(key)) stocked.set(key, value); }
/** Forgets stocked surfaces no figure took. */
export function dropHeadSurfaces(keys: Iterable<string>) { for (const key of keys) stocked.delete(key); }
function remember(key: string, build: () => HeadPiece[]) {
  let known = pieces.get(key);
  if (!known) { known = stocked.get(key) ?? build(); installHeadCache([[key, known]]); }
  return known;
}

/** Skin, eyes, lash lines and lips share anatomy; painted eyebrow choices never remesh them. */
export function faceSurfaceParts(p: HumanProportions, detail: HeadDetail): HeadPiece[] {
  return remember(`${headKey(p)}:face:${detail}`, () => {
    const quality = p.headMeshCell ? { ...FACE, cell: p.headMeshCell } : FACE;
    const s = sculpt(p), ear = p.skull.ear;
    const width = Math.max(ear.pointed ? .125 : .1, ear.x + (ear.pointed ? ear.pointOutward ?? .03 : ear.thickness) + .012);
    const noseReach = s.tip[2] + .02 * p.face.nose * Math.max(1, p.skull.nose.tipWidth ?? 1, p.skull.nose.tipHeight ?? 1);
    const mesh = surface((x, y, z) => s.head.field(x, y, z), [-width, 1.68, Math.min(-.125, ear.z - (ear.pointBack ?? .005) - .02)], [width, Math.max(2.025, ear.y + (ear.pointLength ?? .048) + .012), Math.max(.14, noseReach)], detail, quality);
    const rows = PIECE_ROWS[detail];
    return [fieldPiece('face', mesh, faceTint(s)), ...eyeballs(s.eye, rows), lashes(s), lowerLids(s), lips(s, rows)];
  });
}

export function eyebrowSurfaceKey(p: HumanProportions) { return `${headKey(p)}:eyebrows:${JSON.stringify(p.skull.eyebrows ?? {})}`; }
export function eyebrowParts(p: HumanProportions): HeadPiece[] {
  return remember(eyebrowSurfaceKey(p), () => p.skull.eyebrows?.thickness === 0 ? [] : [brows(sculpt(p), p.skull.eyebrows)]);
}
// Composite views reference the same immutable pieces; only surface entries cross the worker boundary or get baked.
const faceViews = new Map<string, HeadPiece[]>();
/** The rig's own face, with its chosen painted eyebrows. Face shapes warp all pieces together (`faceWarp`). */
export function faceParts(p: HumanProportions, detail: HeadDetail): HeadPiece[] {
  const base = faceSurfaceParts(p, detail), eyebrows = eyebrowParts(p), key = `${eyebrowSurfaceKey(p)}:${detail}`;
  const known = faceViews.get(key);
  if (known && known[0] === base[0] && (eyebrows.length === 0 || known.includes(eyebrows[0]))) return known;
  // Preserve piece ordering for material assembly and existing asset consumers.
  const paintAt = base.length - 3; // The final pieces are lashes, lower lids and lips.
  const result = [...base.slice(0, paintAt), ...eyebrows, ...base.slice(paintAt)];
  if (faceViews.size >= 96) faceViews.delete(faceViews.keys().next().value!);
  faceViews.set(key, result); return result;
}

/** Painted shading on the skin: nostrils, the inside of the ears, the hollows round the eyes, a flush on the cheeks. */
function faceTint(s: Sculpt) {
  const { eye } = s;
  return (x: number, y: number, z: number, out: number[]) => {
    const ax = Math.abs(x);
    const nostril = s.nostril.field(x, y, z);
    if (nostril < .002) shade(out, .65 * smooth(.002 - nostril, 0, .0022), [.36, .22, .2]);
    const concha = s.concha.field(x, y, z);
    if (concha < .002) shade(out, .25 * smooth(.002 - concha, 0, .003), [.8, .55, .5]);
    // The socket darkens toward the inner corner and under the brow.
    const du = ax - eye.x, dv = y - eye.y;
    if (Math.abs(du) < .03 && Math.abs(dv) < .02 && z > .06) {
      shade(out, .16 * Math.exp(-(((du + .006) / .012) ** 2) - ((dv - .007) / .006) ** 2), [.62, .48, .48]);
      // The fold of the upper lid, a soft shadow under the brow.
      shade(out, .26 * Math.exp(-((du / .013) ** 2) - ((dv - eye.up - .0058 + du * du * 2.5) / .0022) ** 2), [.7, .55, .52]);
      shade(out, .12 * Math.exp(-(((du + .012) / .006) ** 2) - (dv / .006) ** 2), [.6, .44, .44]);
    }
    // Blood shows warm in the cheeks and at the tip of the nose, more on a woman.
    shade(out, (s.female ? .1 : .06) * Math.exp(-(((ax - .045) / .016) ** 2) - ((y - 1.822) / .013) ** 2), [1, .76, .74]);
    shade(out, .06 * Math.exp(-((x / .012) ** 2) - ((y - s.tip[1]) / .01) ** 2), [1, .8, .78]);
  };
}

/** A cap of a sphere about its forward (+z) pole, between two angles from that pole. */
function cap(centre: Vec, r: number, from: number, to: number, rows: number, columns: number, material: HeadMaterial, tint?: (polar: number, n: Vec) => Vec): HeadPiece {
  const points: Vec[][] = [], normals: Vec[][] = [], colors: Vec[][] = [];
  for (let i = 0; i <= rows; i++) {
    const polar = mix(from, to, i / rows), row: Vec[] = [], normal: Vec[] = [], color: Vec[] = [];
    for (let j = 0; j <= columns; j++) {
      const azimuth = j / columns * Math.PI * 2, n: Vec = [Math.sin(polar) * Math.cos(azimuth), Math.sin(polar) * Math.sin(azimuth), Math.cos(polar)];
      row.push([centre[0] + n[0] * r, centre[1] + n[1] * r, centre[2] + n[2] * r]); normal.push(n); color.push(tint?.(polar, n) ?? [1, 1, 1]);
    }
    points.push(row); normals.push(normal); colors.push(color);
  }
  return gridPiece(material, points, normals, tint ? colors : undefined);
}
/** How many of their rows and columns the eyeballs and lips keep at each detail: their grids are fixed, not meshed from the field. */
const PIECE_ROWS: Record<HeadDetail, number> = { high: .6, low: .35 };
const rowsOf = (n: number, share: number) => Math.max(2, Math.round(n * share));
/** Eyeballs behind the lid openings: white, an iris darker at its rim and a pupil, looking straight ahead. */
function eyeballs(e: Eye, rows = 1): HeadPiece[] {
  // The iris is a little under half the eye's radius across, the pupil a third of the iris.
  const iris = Math.asin(.44), pupil = Math.asin(.155);
  return [-1, 1].flatMap(side => {
    const c: Vec = [side * e.x, e.y, e.z];
    return [
      cap(c, e.r, iris * .9, Math.PI * .75, rowsOf(14, rows), rowsOf(28, rows), 'ivory', (polar, n) => { const v = mix(1, .8, smooth(polar, .45, 1.2)) * mix(1, .78, smooth(n[1], .05, .45)); return [v, v, v * 1.02]; }),
      cap(c, e.r * 1.0015, 0, iris, rowsOf(8, rows), rowsOf(28, rows), 'eye', polar => { const v = mix(1.0, .55, smooth(polar, iris * .55, iris)) * mix(.8, 1, smooth(polar, pupil, pupil * 1.8)); return [v, v, v]; }),
      cap(c, e.r * 1.003, 0, pupil, rowsOf(3, rows), rowsOf(20, rows), 'dark'),
    ];
  });
}

/**
 * A layer painted on the surface: a 2-D outline in the face's front view (x, y), sampled on a grid, laid onto the
 * skin from the front and lifted off it by `lift`.
 */
type Projector = (x: number, y: number) => { point: Vec; normal: Vec } | undefined;
/** Lays points onto a field's surface from the front. */
const onField = (f: Field): Projector => (x, y) => { const z = front(f, x, y); return z === undefined ? undefined : { point: [x, y, z], normal: gradient(f, x, y, z) }; };
function decal(surface: Field | Projector, outline: (u: number, v: number) => [number, number], columns: number, rows: number, lift: number, material: HeadMaterial, tint?: (u: number, v: number) => Vec): HeadPiece | undefined {
  const project = surface.length === 3 ? onField(surface as Field) : surface as Projector;
  const points: Vec[][] = [], normals: Vec[][] = [], colors: Vec[][] = [];
  for (let i = 0; i <= rows; i++) {
    const row: Vec[] = [], normal: Vec[] = [], color: Vec[] = [];
    for (let j = 0; j <= columns; j++) {
      const u = j / columns, v = i / rows, [x, y] = outline(u, v), hit = project(x, y);
      if (!hit) return undefined;
      const { point: [px, py, pz], normal: n } = hit;
      row.push([px + n[0] * lift, py + n[1] * lift, pz + n[2] * lift]); normal.push(n); color.push(tint?.(u, v) ?? [1, 1, 1]);
    }
    points.push(row); normals.push(normal); colors.push(color);
  }
  return gridPiece(material, points, normals, tint ? colors : undefined);
}
/** Joins pieces of one material into one. */
function joined(material: HeadMaterial, parts: (HeadPiece | undefined)[]): HeadPiece {
  const list = parts.filter((part): part is HeadPiece => !!part), withColors = list.some(part => part.colors);
  let offset = 0;
  const positions: number[] = [], normals: number[] = [], colors: number[] = [], indices: number[] = [];
  for (const part of list) {
    positions.push(...part.positions); normals.push(...part.normals);
    if (withColors) colors.push(...(part.colors ?? new Float32Array(part.positions.length).fill(1)));
    for (const index of part.indices) indices.push(index + offset);
    offset += part.positions.length / 3;
  }
  return { material, positions: new Float32Array(positions), normals: new Float32Array(normals), indices: new Uint32Array(indices), colors: withColors ? new Float32Array(colors) : undefined };
}

/**
 * Where a head's features sit, in skull space before any face warp: the right eye (its centre, radius, half width and lid heights), the mouth
 * (its line and half width), the nose tip and the bottom of the nose, and the nose's width. Marks laid over the skin (`face-marks.ts`) are drawn about these.
 */
export interface FaceLandmarks { eye: Eye; mouth: { y: number; w: number }; tip: Vec; noseBase: number; noseWidth: number; female: boolean }
export function faceLandmarks(p: HumanProportions): FaceLandmarks {
  const s = sculpt(p);
  return { eye: { ...s.eye }, mouth: { y: s.mouth.y, w: s.mouth.w }, tip: [...s.tip], noseBase: s.noseBase, noseWidth: .017 * p.face.nose, female: s.female };
}
/**
 * A strip of skin laid over the face (material `face`, so it joins the face's own batch and draws in the same call): an outline in the front
 * view, sampled on a grid and lifted off the skin by `lift` (a share of it per point, from `rise`), its colour `tint` over the skin's own painted
 * shading, so its edges match the skin beneath. Returns nothing if the outline leaves the face.
 */
export function skinDecal(p: HumanProportions, outline: (u: number, v: number) => [number, number], columns: number, rows: number, lift: number,
  tint: (u: number, v: number) => Vec, rise: (u: number, v: number) => number = () => 1): HeadPiece | undefined {
  const s = sculpt(p), f = s.head.field, paint = faceTint(s), skin = [1, 1, 1];
  const points: Vec[][] = [], normals: Vec[][] = [], colors: Vec[][] = [];
  for (let i = 0; i <= rows; i++) {
    const row: Vec[] = [], normal: Vec[] = [], color: Vec[] = [];
    for (let j = 0; j <= columns; j++) {
      const u = j / columns, v = i / rows, [x, y] = outline(u, v), z = front(f, x, y);
      if (z === undefined) return undefined;
      const n = gradient(f, x, y, z), up = lift * rise(u, v), c = tint(u, v);
      skin[0] = skin[1] = skin[2] = 1; paint(x, y, z, skin);
      row.push([x + n[0] * up, y + n[1] * up, z + n[2] * up]); normal.push(n); color.push([c[0] * skin[0], c[1] * skin[1], c[2] * skin[2]]);
    }
    points.push(row); normals.push(normal); colors.push(color);
  }
  return gridPiece('face', points, normals, colors);
}
/** Joins pieces of one material into one (`joined`), for marks built outside this file. */
export const joinHeadPieces = joined;

/** Paint follows the same sculpt and face warp as the skin; it never floats on a flat card. */
export function makeupParts(p: HumanProportions, id: string): HeadPiece[] {
  if (id === 'none') return [];
  return remember(`${headKey(p)}:makeup:${id}`, () => {
    const s = sculpt(p), e = s.eye, lid = lidLine(e), f = s.head.field;
    const eyePaint: (HeadPiece | undefined)[] = [];
    const paintedLips = { ...lips(s), material: 'lipstick' as const };
    if (id === 'lips') return [paintedLips];
    const soft = id === 'soft', smoky = id === 'smoky' || id === 'ritual', cat = id === 'cat-eye';
    const reach = soft || smoky ? .95 : cat ? 2.15 : 1.65, wingRise = soft || smoky ? 0 : cat ? .68 : .48;
    for (const side of [-1, 1]) {
      const above = (u: number) => {
        const x = mix(-e.w * .95, e.w * reach, u), edge = lid.upper(clamp(x, -e.w * .95, e.w * .95));
        return [side * (e.x + x), e.y + e.tilt * x + edge + .001 + Math.max(0, x - e.w * .65) * wingRise] as [number, number];
      };
      eyePaint.push(decal(f, (u, v) => { const [x, y] = above(u); return [x, y + v * (smoky ? .012 : soft ? .0035 : .0065) * Math.sin(Math.PI * u)]; }, 48, 6, .00065, 'makeup'));
      if (!soft) eyePaint.push(decal(f, (u, v) => { const [x, y] = above(u); return [x, y + v * (cat ? .0028 : .0013) * Math.sin(Math.PI * u)]; }, 48, 2, .0009, 'liner'));
      if (smoky || cat || id === 'double-wing') eyePaint.push(decal(f, (u, v) => {
        const x = mix(-e.w * .92, e.w * (id === 'double-wing' ? 1.9 : .95), u), edge = lid.lower(clamp(x, -e.w * .92, e.w * .95));
        return [side * (e.x + x), e.y + e.tilt * x + edge - .001 - Math.max(0, x - e.w * .6) * .3 - v * (smoky ? .007 : .002) * Math.sin(Math.PI * u)];
      }, 40, 4, .00075, smoky ? 'makeup' : 'liner'));
      if (!soft && !smoky) eyePaint.push(decal(f, (u, v) => [side * (e.x + mix(-e.w * .65, e.w * .7, u)), e.y + .012 + Math.sin(Math.PI * u) * .002 + v * .001], 24, 2, .0008, 'accent'));
      if (id === 'double-wing') eyePaint.push(decal(f, (u, v) => { const [x, y] = above(u); return [x, y + .008 + v * .0015 * Math.sin(Math.PI * u)]; }, 40, 2, .0008, 'accent'));
      if (id === 'warpaint') for (let stripe = 0; stripe < 3; stripe++) eyePaint.push(decal(f, (u, v) =>
        [side * (.024 + u * .034), e.y - .023 - stripe * .007 + u * .006 + (v - .5) * .0035], 28, 2, .0008, 'accent'));
      if (id === 'harlequin') eyePaint.push(decal(f, (u, v) => [side * (.036 + (u - .5) * .02 * Math.sin(Math.PI * v)), e.y - .018 - v * .037], 16, 20, .0008, 'makeup'));
      if (id === 'ritual') eyePaint.push(decal(f, (u, v) => [side * (.018 + u * .045), e.y - .023 + u * .004 - v * .011], 32, 4, .0008, 'accent'));
      if (id === 'sunburst') for (let ray = 0; ray < 5; ray++) {
        const a = ray / 4 * Math.PI, cx = e.x + Math.cos(a) * .027, cy = e.y + Math.sin(a) * .022;
        eyePaint.push(decal(f, (u, v) => [side * (cx + Math.cos(a) * u * .009 + (v - .5) * .0015), cy + Math.sin(a) * u * .009], 12, 2, .0008, 'accent'));
      }
    }
    if (id === 'ritual') eyePaint.push(decal(f, (u, v) => [(u - .5) * .008, 1.895 + v * .035], 4, 20, .0008, 'accent'));
    return [...eyePaint.filter((piece): piece is HeadPiece => !!piece), paintedLips];
  });
}

/** Face ink: one patch on the skin, painted with the design's texture (see `tattoo-ink.ts`). */
export function tattooParts(p: HumanProportions, id: string, scale = 1): HeadPiece[] {
  const preset = humanTattoos.find(preset => preset.id === id);
  if (!preset?.ink) return [];
  return remember(`${headKey(p)}:tattoo-patch:${id}:${scale}`, () => {
    const f = sculpt(p).head.field, forehead = preset.placement === 'forehead', columns = 18, rows = 18;
    const cx = forehead ? 0 : .047, cy = forehead ? 1.905 : 1.817, w = (forehead ? .032 : .022) * scale, h = (forehead ? .032 : .03) * scale;
    const at = (t: number) => (t * (1 + 2 * INK_MARGIN) - INK_MARGIN) - .5;
    // A large cheek piece can reach past the jaw's edge: those points slide in toward the middle until they meet skin, in the clear margin.
    const skin = onField(f), project: Projector = (x, y) => {
      for (let k = 0; k <= 8; k++) { const hit = skin(mix(x, cx, k / 8), mix(y, cy, k / 8)); if (hit) return hit; }
      return undefined;
    };
    const patch = decal(project, (u, v) => [cx + at(u) * w, cy + at(v) * h], columns, rows, .00075, 'tattoo');
    if (!patch) return [];
    patch.uvs = new Float32Array(Array.from({ length: (rows + 1) * (columns + 1) }, (_, i) => [(i % (columns + 1)) / columns, Math.floor(i / (columns + 1)) / rows]).flat());
    return [patch];
  });
}

/**
 * Brows: a solid arch with ragged edges over the brow ridge, combed into hair by lighter and darker streaks that
 * stand up at the inner end and lie outward along the arch. The default is low and straight; the feminine brow is finer.
 */
function brows(s: Sculpt, style?: HumanSkull['eyebrows']): HeadPiece {
  const f = s.head.field, female = s.female, eye = s.eye;
  const from = eye.x - eye.w * .78, to = eye.x + eye.w * 1.95;
  const lower = (t: number) => {
    const arch = style?.angular ? t < .58 ? t / .58 : (1 - t) / .42 : Math.sin(Math.PI * Math.min(1, t * 1.25) ** .8);
    return 1.8768 + (female ? .0052 : .0028) * (style?.arch ?? .12) * arch - .0028 * t ** 2 + (style?.slope ?? (style?.arch === undefined ? .0012 : 0)) * t;
  };
  const height = (t: number) => (style?.thickness ?? 1) * mix(female ? .0042 : .0058, female ? .0012 : .0018, smooth(t, .05, 1)) * (1 - .25 * smooth(.08 - t, 0, .08));
  return joined('brow', [-1, 1].map(side => decal(f, (u, v) => {
    const t = u, x = mix(from, to, t), ragged = .00035 * Math.sin(u * 140) * Math.sin(u * 37 + 1);
    return [side * x, lower(t) + mix(-.0002, height(t) + ragged, v)];
  }, 72, 3, .0005, 'brow', (u, v) => {
    // Streaks lean with the hair: upright near the nose, flat toward the tail.
    const lean = mix(1.8, .25, smooth(u, 0, .45)), streak = .5 + .5 * Math.sin(u * 240 + v * lean * 6);
    const c = mix(.78, 1, streak) * mix(.92, 1, v); return [c, c, c];
  })));
}
/**
 * Lays points onto the skin round an eye, or onto the eyeball inside the lid opening, so a strip painted across the
 * edge of the lids draws that edge as a clean curve however coarse the skin's mesh is.
 */
function aroundEye(s: Sculpt, side: number): Projector {
  const { eye } = s, line = lidLine(eye), skin = onField(s.head.field.bind(s.head));
  return (x, y) => {
    const u = side * x - eye.x, v = y - eye.y - eye.tilt * u;
    if (line.distance(u, v) > 0) return skin(x, y);
    const dx = x - side * eye.x, dy = y - eye.y, depth = eye.r * eye.r - dx * dx - dy * dy;
    if (depth <= 0) return skin(x, y);
    const z = eye.z + Math.sqrt(depth), normal: Vec = [dx / eye.r, dy / eye.r, (z - eye.z) / eye.r];
    return { point: [x, y, z], normal };
  };
}
/**
 * A dark line of lashes along each upper lid, swept into a short wing on the feminine eye, and the rim of the lower
 * lid; both reach a little over the eyeball, so the lids' edges stay crisp.
 */
function lashes(s: Sculpt): HeadPiece {
  const { eye } = s, line = lidLine(eye), end = s.female ? 1.24 : 1.04;
  return joined('dark', [-1, 1].map(side => decal(aroundEye(s, side), (u, v) => {
    const along = mix(-.98, end, u) * eye.w, edge = along > eye.w ? line.upper(eye.w) + (along - eye.w) * .62 : line.upper(along);
    const thick = mix(.0006, s.female ? .0016 : .0012, smooth(u, 0, .7)) * (along > eye.w ? 1 - smooth(along, eye.w, end * eye.w) * .8 : 1);
    return [side * (eye.x + along), eye.y + edge - .0007 + v * (thick + .0007) + eye.tilt * along];
  }, 30, 2, .0003, 'dark')));
}
function lowerLids(s: Sculpt): HeadPiece {
  const { eye } = s, line = lidLine(eye);
  return joined('face', [-1, 1].map(side => decal(aroundEye(s, side), (u, v) => {
    const along = mix(-.98, .99, u) * eye.w, edge = line.lower(along);
    return [side * (eye.x + along), eye.y + edge + .0006 - v * .0019 + eye.tilt * along];
  }, 30, 2, .00025, 'face', (u, v) => { const c = mix(.74, .96, v) * mix(1, .9, Math.abs(u * 2 - 1) ** 4); return [c, c * .86, c * .84]; })));
}
/**
 * The skin of the mouth, seen from just in front of the bare face: a long nose hanging in front of the lips is not where their paint or teeth
 * land.
 */
function mouthSkin(s: Sculpt): Projector {
  const skin = s.head.field.bind(s.head), bare = s.bare.field.bind(s.bare);
  return (x, y) => {
    const near = front(bare, x, y), z = front(skin, x, y, near === undefined ? .16 : near + .008);
    return z === undefined ? undefined : { point: [x, y, z], normal: gradient(skin, x, y, z) };
  };
}
/**
 * Lips with a bowed upper lip and a fuller lower lip, and the line between them. The paint is feathered: its colours carry shade in red and
 * coverage in green (see `featherPaint` where the material is made), so each lip fades into the skin at its outer edge and its corners
 * instead of ending on a drawn outline.
 */
function lips(s: Sculpt, rows = 1): HeadPiece {
  const { mouth } = s, f = mouthSkin(s), w = mouth.w, { line, upper, lower } = lipShape(mouth);
  const across = (u: number) => (u * 2 - 1) * w * .97;
  // Each lip's paint reaches past the mouth line over the other's by `overlap`, so the mouth stays closed whatever the crease does; rows crowd toward the mouth line, where the surface turns sharply.
  const { overlap: ov, lift } = mouth, near = (v: number) => v ** 1.6;
  // Full colour over most of each lip, fading over its outer edge and toward the corners.
  const cover = (u: number, v: number) => (1 - smooth(v, .78, 1)) * (1 - smooth(Math.abs(u * 2 - 1), .8, 1));
  const top = decal(f, (u, v) => { const x = across(u); return [x, line(x) - ov + near(v) * (upper(x) + ov)]; }, rowsOf(48, rows), rowsOf(12, rows), lift + .00005, 'lips', (u, v) => [mix(.84, 1, smooth(v, 0, .5)), cover(u, v), 0]);
  const bottom = decal(f, (u, v) => { const x = across(u); return [x, line(x) + ov - near(v) * (lower(x) + ov)]; }, rowsOf(48, rows), rowsOf(12, rows), lift, 'lips', (u, v) => [mix(.9, 1.04, smooth(v, .1, .55)) * mix(.95, 1, 1 - Math.abs(u * 2 - 1)), cover(u, v), 0]);
  const slit = decal(f, (u, v) => { const x = across(u) * .98; return [x, line(x) + (v - .5) * .0012]; }, rowsOf(48, rows), 1, lift + .0002, 'lips', u => [mix(.5, .7, Math.abs(u * 2 - 1) ** 3), 1 - smooth(Math.abs(u * 2 - 1), .8, 1), 0]);
  return joined('lips', [top, bottom, slit]);
}

/**
 * The thinnest shell of hair or beard each mesh holds. A shell a few millimetres thick meshed on a grid as coarse as itself breaks up
 * into patches of skin, so thin hair grows to this on the coarser meshes; thick hair keeps its own thickness (strand shading keeps
 * either reading as hair).
 */
const THINNEST: Record<HeadDetail, number> = { high: .0062, low: .009 };
/** `thickness`, grown smoothly to at least the thinnest shell the mesh holds. */
const shell = (thickness: number, detail: HeadDetail) => { const least = THINNEST[detail]; return -smin(-thickness, -least, least * .5); };
const HAIR: Quality = { cell: .0065, reduced: { high: 1500, low: 400 } };
/** A value eased between keys of [at, value], sorted by `at`. */
function keyed(keys: [number, number][], at: number) {
  let i = 1; while (i < keys.length - 1 && at > keys[i][0]) i++;
  const [a, va] = keys[i - 1], [b, vb] = keys[i];
  return mix(va, vb, smooth(at, a, b));
}
/** Round the head from the front: the angle about an upright axis through the head, 0 straight ahead, π behind. */
const aroundHead = (x: number, z: number) => Math.abs(Math.atan2(x, z + .012));
/** A fringe: how far the front of the hairline drops, how long its locks are, how many there are per radian and how far they lean. */
interface Fringe { drop: number; length: number; density: number; lean: number }
/** A short crop falls lower over the forehead; tied and long hair keep the lighter fringe, whose locks taper away to the ears. */
const LOOSE_FRINGE: Fringe = { drop: .014, length: .02, density: 6, lean: 5 };
const TIED_FRINGE: Fringe = { drop: .008, length: .012, density: 6, lean: 5 };
/**
 * Where hair grows: above a line round the head, high at the forehead, receding a little at the temples, down to
 * the sideburns in front of the ears, up over the ears and down to the nape. Negative inside, about the height
 * above the line outside; the ears themselves stay bare.
 */
function hairline(front: number, sideburn: number, nape: number, fringe?: Fringe) {
  // Use a distance in skull units: a normalized ellipse multiplied by its short radius left a broad bald halo.
  const clearance = paired(ellipsoid([.0738, 1.843, -.013], [.008, .0265, .0185], [-.18, -.32, 0]));
  // Without a fringe the hair stops at a clean line across the forehead and drops steeply at the temple. With one the line sits lower and
  // slopes down the temples to the sideburn, and its edge breaks into locks that lean toward the ears and shorten as they go.
  const top = front - (fringe?.drop ?? 0);
  const keys: [number, number][] = fringe
    ? [[0, top], [.3, top - .006], [.58, top - .026], [.84, top - .042], [1.02, sideburn + .03], [1.14, sideburn + .01], [1.22, sideburn + .004], [1.31, sideburn + .004], [1.5, 1.856], [1.95, 1.851], [2.3, 1.81], [Math.PI, nape]]
    : [[0, front], [.5, front - .004], [.82, front - .017], [1.06, sideburn + .03], [1.18, sideburn + .004], [1.31, sideburn + .004], [1.5, 1.856], [1.95, 1.851], [2.3, 1.81], [Math.PI, nape]];
  return (x: number, y: number, z: number) => {
    const angle = aroundHead(x, z);
    let line = keyed(keys, angle);
    if (fringe) {
      const phase = (angle - fringe.lean * (y - top)) * fringe.density, lock = Math.floor(phase), tooth = 1 - Math.abs(2 * (phase - lock) - 1);
      line -= fringe.length * (.55 + .45 * hash(lock, 7)) * tooth ** 1.4 * (1 - smooth(angle, .4, 1.1)) * smooth(angle, .02, .12);
    }
    return Math.max(line - y, -clearance.field(x, y, z));
  };
}
/** Strands: ridges along the hair, as a value from -1 to 1. Hair falls from a crown behind the top of the head. */
const strands = (x: number, y: number, z: number, count = 34) => {
  const fall = Math.atan2(x, z + .035);
  return .7 * Math.sin(fall * count + Math.sin(y * 160) * .9) + .3 * Math.sin(fall * count * 2.6 + y * 120);
};
const tint = (along: (x: number, y: number, z: number) => number) => (x: number, y: number, z: number, out: number[]) => { const c = .76 + .2 * along(x, y, z); out[0] = out[1] = out[2] = c; };
/** Hair resting on the back: how much of each point the torso carries instead of the head. */
const onBack = (p: HumanProportions, y: number) => {
  const shift = p.shoulderY - (p.motionRig === 'human-female' ? 1.556 : 1.598);
  return 1 - smooth(y, 1.64 + shift, 1.79 + shift);
};

/** Where a ray from `from` along the unit vector `dir` first meets a field's surface, or undefined. */
function march(f: Field, from: Vec, dir: Vec, limit = .5): Vec | undefined {
  for (let t = 0, i = 0; i < 500 && t < limit; i++) {
    const x = from[0] + dir[0] * t, y = from[1] + dir[1] * t, z = from[2] + dir[2] * t, d = f(x, y, z);
    if (d < 2e-6) return [x, y, z];
    t += Math.max(d * .8, 1e-5);
  }
  return undefined;
}
/** A point on a smooth (Catmull-Rom) path through `points`, at `t` from 0 to 1. */
function pathAt(points: readonly Vec[], t: number): Vec {
  const spans = points.length - 1, at = clamp(t, 0, 1) * spans, i = Math.min(spans - 1, Math.floor(at)), u = at - i;
  const p0 = points[Math.max(0, i - 1)], p1 = points[i], p2 = points[i + 1], p3 = points[Math.min(spans, i + 2)];
  return [0, 1, 2].map(c => .5 * (2 * p1[c] + (p2[c] - p0[c]) * u + (2 * p0[c] - 5 * p1[c] + 4 * p2[c] - p3[c]) * u * u + (3 * p1[c] - p0[c] - 3 * p2[c] + p3[c]) * u ** 3)) as Vec;
}
/** The skin's distance from the upright axis through the head at a height, along `angle` from straight ahead. */
function skinRadiusOf(head: Field, angle: number, y: number) {
  let r = .18;
  for (let i = 0; i < 300 && r > .01; i++) {
    const d = head(Math.sin(angle) * r, y, Math.cos(angle) * r - .012);
    if (d < 2e-6) break;
    r -= Math.max(d * .75, 1e-5);
  }
  return r;
}
/** How loose hair flows: ridges running down it, as a value from -1 to 1. */
const flow = (x: number, y: number, z: number) => .75 * Math.sin(aroundHead(x, z) * 52 + .5 * Math.sin((y - 1.65) * 36)) + .25 * Math.sin(aroundHead(x, z) * 105 + y * 22);
/** The neck and shoulders hair rests on, a rough volume in skull space, so hanging hair stays clear of the body. */
function bodyAt(p: HumanProportions): Field {
  const feminine = p.motionRig === 'human-female', shift = p.shoulderY - (feminine ? 1.556 : 1.598), breadth = p.shoulderHalfWidth / (feminine ? .157 : .201);
  const shoulders = union(.04, roundCone([0, 1.6 + shift, -.012], [0, 1.74 + shift, -.012 + p.neckZ], .07, .058), ellipsoid([0, 1.565 + shift, -.015], [.2 * breadth, .07, .1]));
  return (x, y, z) => shoulders.field(x * p.headScale, p.headY + (y - HUMAN_HEAD_ORIGIN) * p.headScale, p.headZ + z * p.headScale) / p.headScale;
}
/**
 * How far forward the chest reaches in skull space at a sideways offset and height, from the rig's own trunk profile (the bust included),
 * so hair that lies on the chest rests just off it on either body.
 */
function chestFront(p: HumanProportions): (x: number, y: number) => number {
  const h = p.hipsY, s = p.shoulderY, neckBase = s + HUMAN_NECK_BASE_RISE;
  const rows = [h - .1, h - .03, h + .06, h + .16, h + .27, s - .19, s - .115, s - .05, s - .01, s + .02, s + .049, s + .059, neckBase];
  const forward = [0, 0, 0, 0, 0, p.bust, p.bust * 1.6, p.bust * .4];
  return (x, y) => {
    const rigY = p.headY + (y - HUMAN_HEAD_ORIGIN) * p.headScale;
    let i = 1; while (i < rows.length - 1 && rigY > rows[i]) i++;
    const t = clamp((rigY - rows[i - 1]) / (rows[i] - rows[i - 1]), 0, 1);
    const front = mix(p.trunk.front[i - 1] + (forward[i - 1] ?? 0), p.trunk.front[i] + (forward[i] ?? 0), t), back = mix(p.trunk.back[i - 1], p.trunk.back[i], t);
    // The neck rows lean back; the surface falls away toward the sides of the chest.
    const centre = (front - back) / 2 + p.neckZ * smooth(rigY, s - .05, neckBase), across = Math.min(.98, Math.abs(x * p.headScale) / (rigY < s - .05 ? p.chestHalfWidth : p.shoulderHalfWidth * .9));
    const dome = p.bust ? p.bust * 2.9 * Math.exp(-(((rigY - (s - .13)) / .07) ** 2)) : 0;
    return (centre + (front + back) / 2 * (1 - across ** 2.3) ** (1 / 2.3) + dome - p.headZ) / p.headScale;
  };
}
/**
 * How freely hair swings below a height: held down to `from`, free by `to`. Hair in front of the body (locks on the chest) is negative:
 * it swings from side to side only (see `hair-sway.ts`).
 */
const hangsFrom = (from: number, to: number) => (_x: number, y: number, z: number) => smooth(from - y, 0, from - to) * (z > .005 ? -.6 : 1);

/** Locks hanging from the scalp round the back and sides, from `from` to `to` radians off straight ahead. */
function hangingLocks(skin: (angle: number, y: number) => number, count: number, from: number, to: number, end: (i: number, side: number) => number, out: number, radii = [.012, .018, .017, .014, .0065]): Shape[] {
  return Array.from({ length: count }, (_, i) => {
    const angle = mix(from, to, i / (count - 1)), side = Math.sin(angle);
    const at = (r: number, y: number): Vec => [Math.sin(angle) * r, y, Math.cos(angle) * r - .012];
    const upper = skin(angle, 1.855) + out * .7, lower = Math.max(upper, skin(angle, 1.78) + out), tail = end(i, side);
    // Thinner at the sides, so the hair does not stand out past the head there.
    const taper = 1 - .32 * side * side;
    return chain([at(skin(angle, 1.914) + .006, 1.914), at(upper, 1.855), at(lower, 1.78), at(lower + .002, 1.705), at(lower - .003, tail)], radii.map(r => r * taper), .005);
  });
}
/** A lock falling from the temple, in front of the ear and along the cheek, down to height `end`. */
function faceLock(skin: (angle: number, y: number) => number, side: number, end: number, scale = 1): Shape {
  const radii = [.0065, .0095, .009, .0072, .004].map(r => r * scale), angles = [1.13, 1.07, 1.03, 1.0, .985], heights = [1.905, 1.855, mix(1.855, end, .45), mix(1.855, end, .8), end];
  // The lock lies on the cheek at its root and hangs a little clear of it toward the tip.
  return chain(heights.map((y, i): Vec => { const r = skin(angles[i], y) + radii[i] * .6 + .006 * (i / 4) ** 2; return [side * Math.sin(angles[i]) * r, y, Math.cos(angles[i]) * r - .012]; }), radii, .004);
}
/** Locks falling from the temple over the front of the shoulder onto the chest, a broad one and a finer one beside it. */
function frontLocks(skin: (angle: number, y: number) => number, chest: (x: number, y: number) => number, side: number, scale = 1): Shape[] {
  const at = (angle: number, y: number, off: number): Vec => { const r = skin(angle, y) + off; return [side * Math.sin(angle) * r, y, Math.cos(angle) * r - .012]; };
  /** On the chest at a sideways offset and height, `gap` clear of it. */
  const on = (x: number, y: number, gap: number): Vec => [side * x, y, chest(x, y) + gap];
  const broad = chain([at(1.12, 1.905, .004), at(1.06, 1.85, .006), at(1.01, 1.79, .009), [side * .074, 1.73, .03], on(.086, 1.7, .018), on(.094, 1.66, .018), on(.098, 1.62, .018), on(.098, 1.575, .018)],
    [.0055, .0085, .0085, .0085, .0085, .0085, .0075, .0035].map(r => r * scale), .004);
  const fine = chain([at(1.1, 1.89, .003), at(1.03, 1.82, .007), [side * .068, 1.74, .034], on(.078, 1.705, .016), on(.083, 1.66, .016), on(.082, 1.615, .016), on(.078, 1.59, .016)],
    [.004, .0065, .0065, .0065, .0065, .006, .003].map(r => r * scale), .004);
  return [broad, fine];
}

/** A curtain of loose hair: how far it stands out, how thick a sheet it is, where it opens round the face and where its ends fall. */
interface Curtain {
  /** How far the outside of the hair stands off the widest part of the head above. */
  out: number; thick: number;
  /** The face stays open in front of this angle round the head (radians from straight ahead). */
  open: number;
  /** Where the ends fall beside the face and at the middle of the back. */
  endFront: number; endBack: number;
  /** How many locks break up the ends and the surface round half the head, and how much their ends vary. */
  locks: number; ragged: number;
}
/**
 * Loose hair falling under its own weight: below the crown each side hangs straight down from the widest part of the head above it,
 * so it covers the ears and drops beside the face and neck as one sheet instead of tracing the skull like a cap. Behind, it rests on the
 * back; at the sides it ends above the shoulders rather than standing out over them. The face stays open. The outline is a table of
 * radii round an upright axis through the head, measured once from the actual head and body, so the field is cheap to sample.
 */
function curtainHair(head: Field, body: Field, c: Curtain): Field & { rise: Field } {
  const angles = 48, low = 1.44, high = 1.93, step = .005, rows = Math.round((high - low) / step) + 1;
  const radius = (f: Field, angle: number, y: number, start: number) => {
    let r = start;
    for (let i = 0; i < 300 && r > .005; i++) { const d = f(Math.sin(angle) * r, y, Math.cos(angle) * r - .012); if (d < 2e-6) return r; r -= Math.max(d * .75, 1e-5); }
    return 0;
  };
  const outer = new Float32Array(angles * rows), end = new Float32Array(angles);
  for (let a = 0; a < angles; a++) {
    const angle = a / (angles - 1) * Math.PI, behind = smooth(angle, 2.1, 2.7);
    let widest = 0, contact = low;
    for (let i = rows - 1; i >= 0; i--) {
      const y = low + i * step;
      widest = Math.max(widest, radius(head, angle, y, .18));
      let r = widest + c.out;
      // Behind, the hair lies on the back; elsewhere it must end before the body reaches it.
      const b = radius(body, angle, y, .32);
      if (behind > 0) r = Math.max(r, mix(r, b + .005, behind));
      if (b + .003 > r - c.thick && contact === low && y < 1.8) contact = y;
      outer[a * rows + i] = r;
    }
    end[a] = Math.max(mix(c.endFront, c.endBack, smooth(angle, c.open, 2.6)), mix(contact + .012, low, behind));
  }
  const table = (values: Float32Array, angle: number, y: number) => {
    const fa = clamp(angle / Math.PI, 0, 1) * (angles - 1), fy = clamp((y - low) / step, 0, rows - 1), a = Math.min(angles - 2, Math.floor(fa)), i = Math.min(rows - 2, Math.floor(fy)), u = fa - a, v = fy - i;
    return mix(mix(values[a * rows + i], values[a * rows + i + 1], v), mix(values[(a + 1) * rows + i], values[(a + 1) * rows + i + 1], v), u);
  };
  const endAt = (angle: number) => { const fa = clamp(angle / Math.PI, 0, 1) * (angles - 1), a = Math.min(angles - 2, Math.floor(fa)); return mix(end[a], end[a + 1], fa - a); };
  const field = (x: number, y: number, z: number) => {
    const angle = aroundHead(x, z), rho = Math.hypot(x, z + .012), r = table(outer, angle, y);
    // Locks: each its own length, the surface grooved between them.
    const phase = angle / Math.PI * c.locks, lock = Math.floor(phase), across = phase - lock, groove = .5 - .5 * Math.cos(across * 2 * Math.PI);
    const tip = endAt(angle) - c.ragged * hash(lock, 11) * smooth(angle, c.open + .2, c.open + .6) * (1 - .6 * groove);
    const along = smooth(y - tip, 0, .06), thick = c.thick * (.35 + .65 * along);
    const surface = rho - (r - .0018 * (1 - groove) * smooth(1.86 - y, 0, .04));
    // Toward the face the sheet thins and turns back a little lower down, so its front edge is a rounded fall of hair, not a cut.
    const open = c.open + .25 * smooth(1.84 - y, 0, .18), edge = smooth(angle, open, open + .35);
    const sheet = Math.max(surface, r - thick * (.45 + .55 * edge) - rho);
    return smax(smax(smax(sheet, tip - y, .004), (open - angle) * rho, .012), y - 1.9, .01);
  };
  return Object.assign(field, { rise: () => 1 });
}

/**
 * The chosen hairstyle as closed volumes over the scalp. Short hair is a shell grown out of the skin above the
 * hairline, thinning to nothing at its edge; longer hair falls in drapes or locks with an open front; tied hair
 * is drawn tight to a tie with its tail, braid or knot. The part under the skin is dropped. Under headwear the hair is
 * pressed in beneath the piece and what it hides is dropped (see `hair-fit.ts`).
 */
export function hairParts(p: HumanProportions, style: string, detail: HeadDetail, fit?: HairFit): HeadPiece[] {
  const built = remember(`${headKey(p)}:hair:${style}:${detail}`, () => buildHair(p, style, detail));
  return fit ? remember(`${headKey(p)}:hair:${style}:${detail}:${fit.key}`, () => fitHair(built, fit)) : built;
}

interface Placement {
  box?: [Vec, Vec]; hanging?: boolean; material?: HeadMaterial; rise?: Field;
  /** How freely each point swings with the body, from 0 where the hair is held to 1 (see `hair-sway.ts`). */
  sway?: Field;
  /** Where the hair is combed to (a tie, a knot, back over the crown); otherwise it falls from the crown. */
  toward?: Vec;
}

/** Behind the top of the head: hair that is not combed anywhere falls away from here. */
const CROWN: Vec = [0, 1.975, -.04];
/** The way strands run: away from the crown, or toward a tie, turning to hang straight down below the head or the tie. */
const hairFlow = (toward?: Vec) => (x: number, y: number, z: number): Vec => {
  const combed = toward && y > toward[1] - .015, hang = toward ? smooth(toward[1] - .015 - y, 0, .04) : smooth(1.905 - y, 0, .13);
  const dx = combed ? toward[0] - x : x - CROWN[0], dy = combed ? toward[1] - y : y - CROWN[1], dz = combed ? toward[2] - z : z - CROWN[2], l = Math.hypot(dx, dy, dz) || 1;
  return [dx / l * (1 - hang), dy / l * (1 - hang) - hang, dz / l * (1 - hang)];
};

function buildHair(p: HumanProportions, style: string, detail: HeadDetail): HeadPiece[] {
  if (style === 'shaved') return [];
  const s = sculpt(p), hairSkin = (p.skull.ear.pointLength ?? 0) > .04 ? s.hairSkin : s.head;
  // Loose hair parts around protruding ear tips even when their roots sit close to the skull.
  const ear = p.skull.ear, tip: Vec = [ear.x + (ear.pointOutward ?? .03), ear.y + (ear.pointLength ?? .048), ear.z - (ear.pointBack ?? .005)];
  const earClearance = ear.pointed && (ear.pointLength ?? 0) > .04
    ? paired(roundCone([mix(ear.x, tip[0], .6), mix(ear.y, tip[1], .6), mix(ear.z, tip[2], .6)], tip, .021, .018)) : undefined;
  const head = (x: number, y: number, z: number) => hairSkin.field(x, y, z), female = s.female, coarse = (t: number) => shell(t, detail);
  // Attachment points follow the actual skull surface rather than assuming the old head's depth and crown height.
  const backAt = (y: number) => -(front((x, y, z) => head(x, y, -z), 0, y, .18) ?? .1);
  const keep = (x: number, y: number, z: number) => s.head.field(x, y, z) > -.0003;
  const bounds: [Vec, Vec] = [[-.13, 1.58, -.2], [.13, 2.06, .13]], low: [Vec, Vec] = [[-.14, 1.46, -.2], [.14, 2.06, .17]];
  const skin = (angle: number, y: number) => skinRadiusOf(head, angle, y), shoulders = bodyAt(p), chest = chestFront(p);
  /** A point on the skull's surface where a line from the middle of the cranium out along `direction` leaves it, with its outward normal. */
  const skullAt = (direction: Vec) => {
    const length = Math.hypot(...direction), d: Vec = [direction[0] / length, direction[1] / length, direction[2] / length], centre: Vec = [0, 1.889, -.01];
    const point = march(head, [centre[0] + d[0] * .3, centre[1] + d[1] * .3, centre[2] + d[2] * .3], [-d[0], -d[1], -d[2]], .4) ?? [centre[0] + d[0] * .09, centre[1] + d[1] * .09, centre[2] + d[2] * .09] as Vec;
    return { point, normal: gradient(head, point[0], point[1], point[2]) };
  };
  /** A shell from just under the skin to `thickness` above it, within `region`, thinning over `feather` at its edge. */
  const grown = (thickness: Field, region: Field, feather = .014): Field & { rise: Field } => Object.assign(
    (x: number, y: number, z: number) => layer(head(x, y, z), coarse(thickness(x, y, z)), region(x, y, z), feather),
    { rise: (x: number, y: number, z: number) => smooth(-region(x, y, z), 0, feather) });
  const tied = (to: Vec) => {
    // Combed toward the tie, with a rounded volume over the scalp rather than a painted-on cap.
    const combed = (x: number, y: number, z: number) => .7 * Math.sin(Math.atan2(x, y - to[1] + .02) * 30 + z * 120) + .3 * Math.sin(Math.atan2(x, y - to[1]) * 70);
    return { along: combed, field: grown((x, y, z) => (.0065 + .0035 * smooth(y, 1.86, 1.955)) * (1 + .1 * combed(x, y, z)), hairline(1.925, 1.831, 1.782, TIED_FRINGE), .012) };
  };
  const pieces: HeadPiece[] = [];
  const add = (field: Field & { rise?: Field }, along: Field, o: Placement = {}) => {
    const [min, max] = o.box ?? bounds, rise = o.rise ?? field.rise, material = o.material ?? 'strands';
    const clear = earClearance ? (x: number, y: number, z: number) => smax(field(x, y, z), -earClearance.field(x, y, z), .003) : field;
    const mesh = surface(clear, min, max, detail, HAIR, keep);
    // The coarse grid cuts the hairline into steps; easing its open edge along itself draws it as one line.
    relaxEdge(mesh, 6);
    if (rise) {
      // Seat the thinnest edge just inside the skin: meshing/simplification must not leave a floating root rim.
      for (let i = 0; i < mesh.positions.length; i += 3) {
        const x = mesh.positions[i], y = mesh.positions[i + 1], z = mesh.positions[i + 2];
        const amount = 1 - smooth(rise(x, y, z), .035, .23), d = head(x, y, z);
        if (amount <= 0 || d < -.0015 || d > .003) continue;
        const n = gradient(head, x, y, z), inset = Math.max(0, d + .0002) * amount;
        mesh.positions[i] -= n[0] * inset; mesh.positions[i + 1] -= n[1] * inset; mesh.positions[i + 2] -= n[2] * inset;
      }
      softenEdge(mesh, head, rise); faceAlong(mesh.positions, mesh.normals, mesh.indices);
    }
    // Thin where the shell meets the scalp at the hairline; anywhere it stands clear of the skin it is thick.
    const strand = material === 'strands' ? { lift: HAIR_LIFT[detail], flow: hairFlow(o.toward), axis: o.toward ? [o.toward[0], o.toward[2]] as [number, number] : undefined, cover: (x: number, y: number, z: number) => Math.max(rise ? smooth(rise(x, y, z), 0, .2) : 1, smooth(head(x, y, z), .0015, .006)) } : undefined;
    if (mesh.positions.length) pieces.push(fieldPiece(material, mesh, material === 'strands' ? tint(along) : undefined, o.hanging ? (_x, y) => onBack(p, p.headY + (y - HUMAN_HEAD_ORIGIN) * p.headScale) : undefined, o.sway, strand));
  };
  /** A leather tie, ring or band: a separate piece, so it takes the leather colour. */
  const bind = (shape: Shape) => { const m = shape.r + .008; add((x, y, z) => shape.field(x, y, z), () => 0, { material: 'leather', box: [[shape.cx - m, shape.cy - m, shape.cz - m], [shape.cx + m, shape.cy + m, shape.cz + m]] }); };
  const tie = (from: Vec, to: Vec, r: number) => bind(roundCone(from, to, r, r));
  const carved = (shape: Shape) => (x: number, y: number, z: number) => Math.max(shape.field(x, y, z), -(head(x, y, z) + .0045));

  if (style === 'buzz') add(grown(() => .0032, hairline(1.928, 1.831, 1.785), .009), (x, y, z) => .5 * strands(x, y, z, 90));
  else if (style === 'crop') {
    // Short at the back and sides, fuller on top and lifted a little at the front.
    const lift = (x: number, y: number, z: number) => .0048 * Math.exp(-((x / .035) ** 2) - ((y - 1.952) / .022) ** 2 - ((z - .055) / .03) ** 2);
    add(grown((x, y, z) => (mix(.0065, female ? .016 : .0145, smooth(y, 1.86, 1.95)) + lift(x, y, z)) * (1 + .12 * strands(x, y, z)), hairline(1.923, 1.827, 1.781, LOOSE_FRINGE), .012), strands);
  } else if (style === 'swept') {
    // Combed back from a high hairline: full over the crown, lying close over the temples and above the ears.
    const back = (x: number, y: number, z: number) => .65 * Math.sin(x * 330 + Math.sin(z * 90) * 1.4) + .35 * Math.sin(x * 760 + y * 80);
    add(grown((x, y, z) => {
      const sides = smooth(Math.abs(x), .028, .066);
      return (.0065 + .0105 * smooth(y, 1.875, 1.955) * (1 - .78 * sides)) * (1 + .12 * smooth(z, -.06, .05) * (1 - sides)) * (1 + .09 * back(x, y, z));
    }, hairline(1.933, 1.832, 1.777), .015), back, { toward: [0, 1.85, -.24] });
  } else if (style === 'tail' || style === 'braid') {
    // Tied at the nape, with a lock escaping at each temple.
    const knot: Vec = [0, 1.877, backAt(1.877) - .004], hair = tied(knot), locks = union(.004, faceLock(skin, -1, 1.785, .85), faceLock(skin, 1, 1.785, .85));
    let hanging: Shape;
    if (style === 'tail') {
      hanging = chain([knot, [0, knot[1] - .018, knot[2] - .019], [0, 1.8, Math.min(-.143, knot[2] - .029)], [0, 1.73, -.141], [0, 1.66, -.13]], [.01, .017, .017, .013, .006], .005);
      tie([0, knot[1] - .003, knot[2] - .005], [0, knot[1] - .012, knot[2] - .014], .0125);
    } else {
      // Plaited lobes alternating side to side down the back of the neck, tied off above a short tuft.
      const offset = knot[2] + .1;
      const lobes = Array.from({ length: 10 }, (_, i) => ellipsoid([(i % 2 ? 1 : -1) * .005, 1.848 - i * .0205, -.119 + offset - .006 * Math.sin(i * .35) - .001 * i], [.0115 - i * .0004, .0135, .0098], [0, 0, (i % 2 ? 1 : -1) * .5]));
      hanging = union(.0045, roundCone(knot, [0, 1.85, -.119 + offset], .01, .011), ...lobes, roundCone([0, 1.638, -.124 + offset], [0, 1.612, -.12 + offset], .0075, .002));
      tie([0, 1.648, -.124 + offset], [0, 1.638, -.124 + offset], .0088);
    }
    const strand = style === 'tail' ? (x: number, y: number, z: number) => Math.max(hanging.field(x, y, z) - .0012 * strands(x, y, z, 40), -(head(x, y, z) + .0045)) : carved(hanging);
    add((x, y, z) => smin(smin(hair.field(x, y, z), strand(x, y, z), style === 'tail' ? .006 : .005), carved(locks)(x, y, z), .004),
      hair.along, { hanging: true, rise: hair.field.rise, sway: hangsFrom(knot[1] - .02, 1.7), toward: knot });
  } else if (style === 'topknot' || style === 'halfup') {
    // The knot sits where the crown turns down toward the back of the head, not on top of it.
    const topknot = style === 'topknot', seat = skullAt([0, topknot ? .74 : .5, topknot ? -.67 : -.86]), [px, py, pz] = seat.point, [nx, ny, nz] = seat.normal;
    const at = (h: number): Vec => [px + nx * h, py + ny * h, pz + nz * h], hair = tied(seat.point);
    const reach = topknot ? .034 : .026, size = topknot ? .038 : .028;
    const bun = union(.012, roundCone(at(.002), at(reach + .004), .02, size), ellipsoid(at(reach + .006), [size * 1.02, size * .86, size * 1.02], [Math.atan2(nz, ny), 0, 0]));
    const knotted = (x: number, y: number, z: number) => smin(hair.field(x, y, z), bun.field(x, y, z) - .0012 * strands(x, y, z, 40), .01);
    if (topknot) add(knotted, hair.along, { rise: hair.field.rise, toward: seat.point });
    else {
      // Half up: the crown gathered in a knot, the rest loose at the back and sides, with a lock at each temple.
      const loose = union(.006, ...hangingLocks(skin, 12, 1.55, 2 * Math.PI - 1.55, (i, side) => 1.64 + .03 * side * side + .008 * Math.cos(i * 1.9), .006), faceLock(skin, -1, 1.77, 1.1), faceLock(skin, 1, 1.77, 1.1));
      add((x, y, z) => Math.max(smin(knotted(x, y, z), smax(loose.field(x, y, z) - .0005 * flow(x, y, z), -(shoulders(x, y, z) - .004), .004), .006), -(head(x, y, z) + .0045)),
        (x, y, z) => .65 * flow(x, y, z), { hanging: true, rise: hair.field.rise, box: low, sway: hangsFrom(1.8, 1.66), toward: seat.point });
    }
    bind(torus(at(.011), seat.normal, size * .62, .0036));
  } else if (style === 'long') {
    // A scalp cap, and below it a curtain falling past the ears to rest on the back, with a lock framing each side of the face.
    // Thinner over the forehead, where it would otherwise end on a thick rim.
    const cap = grown((x, y, z) => (.01 + .009 * smooth(y, 1.86, 1.95)) * mix(.6, 1, smooth(aroundHead(x, z), .25, .9)), hairline(1.922, 1.83, 1.778), .02);
    const curtain = curtainHair(head, shoulders, { out: .013, thick: .017, open: .98, endFront: 1.69, endBack: female ? 1.5 : 1.53, locks: 9, ragged: .03 });
    const frame = union(.004, faceLock(skin, -1, 1.735, 1.25), faceLock(skin, 1, 1.735, 1.25));
    add((x, y, z) => Math.max(smin(smin(cap(x, y, z), curtain(x, y, z), .01), frame.field(x, y, z) - .0004 * flow(x, y, z), .005), -(head(x, y, z) + .0045)),
      (x, y, z) => .65 * flow(x, y, z), { hanging: true, rise: cap.rise, box: low, sway: hangsFrom(1.8, 1.66) });
  } else if (style === 'locks') {
    // A scalp cap feeds overlapping rope locks at the sides and back; the front stays open below the face, apart from the locks that fall
    // from each temple in front of the shoulder.
    const cap = grown(() => .0075, hairline(1.922, 1.83, 1.778, TIED_FRINGE), .012), from = 1.22;
    const hung = hangingLocks(skin, 21, from, 2 * Math.PI - from, (i, side) => 1.585 + .05 * side * side + .035 * hash(i, 3), .008, [.0105, .0125, .0125, .0115, .0065]);
    const fall = union(.005, ...hung, ...[-1, 1].flatMap(side => frontLocks(skin, chest, side, 1.4)));
    add((x, y, z) => {
      const wrap = .0009 * Math.sin(y * 250 + Math.atan2(x, z + .012) * 11);
      return Math.max(smin(cap(x, y, z), smax(fall.field(x, y, z) - wrap, -(shoulders(x, y, z) - .004), .004), .006), -(head(x, y, z) + .0045));
    }, (x, y, z) => .65 * flow(x, y, z), { hanging: true, rise: cap.rise, box: low, sway: hangsFrom(1.8, 1.66) });
  } else if (style === 'twin') {
    // Two plaits, one behind each ear, brought forward over the shoulders and tied off on the chest.
    const cap = grown((_x, y) => .009 + .007 * smooth(y, 1.86, 1.95), hairline(1.922, 1.83, 1.778, TIED_FRINGE), .012);
    const plaits = [-1, 1].map(side => {
      const rootAt = (angle: number, y: number, off: number): Vec => { const r = skin(angle, y) + off; return [side * Math.sin(angle) * r, y, Math.cos(angle) * r - .012]; };
      const on = (x: number, y: number): Vec => [side * x, y, chest(x, y) + .026];
      const path: Vec[] = [rootAt(1.72, 1.86, .006), rootAt(1.7, 1.8, .01), [side * .066, 1.75, -.008], on(.082, 1.712), on(.092, 1.672), on(.1, 1.62), on(.1, 1.57), on(.098, 1.52)];
      const count = 22, lobes: Shape[] = [];
      for (let i = 0; i < count; i++) {
        const t = .12 + .88 * i / (count - 1), centre = pathAt(path, t), ahead = pathAt(path, Math.min(1, t + .02)), behind = pathAt(path, Math.max(0, t - .02));
        const tx = ahead[0] - behind[0], ty = ahead[1] - behind[1], l = Math.hypot(tx, ty) || 1, lateral = Math.abs(ty) > 1e-4 ? [ty / l, -tx / l] : [1, 0];
        const taper = 1 - .45 * smooth(t, .8, 1), swing = (i % 2 ? 1 : -1) * .0045 * taper;
        lobes.push(sphere([centre[0] + lateral[0] * swing, centre[1] + lateral[1] * swing, centre[2]], .0108 * taper));
      }
      const core = chain([0, .25, .5, .75, 1].map(t => pathAt(path, t)), [.011, .0095, .0092, .0085, .005], .004);
      return { shape: union(.004, core, ...lobes), end: pathAt(path, 1), before: pathAt(path, .97) };
    });
    const braids = union(.004, ...plaits.map(plait => plait.shape));
    add((x, y, z) => Math.max(smin(cap(x, y, z), smax(braids.field(x, y, z), -(shoulders(x, y, z) - .004), .004), .006), -(head(x, y, z) + .0045)),
      (x, y, z) => .6 * strands(x, y, z, 24), { hanging: true, rise: cap.rise, box: low, sway: hangsFrom(1.76, 1.56) });
    for (const { end, before } of plaits) bind(torus(end, [end[0] - before[0], end[1] - before[1], end[2] - before[2]], .0085, .0035));
  } else {
    // A parted bob with a full crown and rounded ends at the jaw.
    const end = 1.762, drape = ellipsoid([0, 1.852, -.01], [.0905, .14, .105]);
    const opening = (x: number, y: number, z: number) => {
      const fringe = 1.91 - .026 * smooth(x, -.05, .035) + .008 * (Math.abs(x) / .06) ** 2;
      return smax(smax(.012 - z, ((x / .066) ** 2 + ((clamp(y, 1.78, fringe) - 1.837) / .085) ** 2 - 1) * .03, .01), y - fringe, .006);
    };
    const rootRise = (x: number, y: number, z: number) => mix(1, smooth(opening(x, y, z), 0, .014), smooth(y, 1.861, 1.898));
    const field = (x: number, y: number, z: number) => {
      const d = head(x, y, z);
      const bottom = end + .012 * smooth(z, -.02, .06) - y;
      // At least a little hair over every part of the scalp the drape leaves thin, such as the crown and fringe.
      const body = smin(drape.field(x * (1 + .1 * smooth(1.8 - y, 0, .05)), y, z), d - .011, .004);
      let outer = smax(body - .0008 * strands(x, y, z, 30), bottom, .012);
      // The face stays open below a fringe that sweeps across the forehead from the parting.
      // The forehead/temple edge thins into the scalp; hanging locks retain their volume below the roots.
      outer = mix(d - .0002, outer, rootRise(x, y, z));
      outer = smax(outer, -opening(x, y, z), .002);
      return Math.max(outer, -(d + .0045));
    };
    add(field, (x, y, z) => .7 * strands(x, y, z), { rise: rootRise });
  }
  return pieces;
}
/** A band round the head over the hair. */
export function bandParts(p: HumanProportions, detail: HeadDetail): HeadPiece[] {
  return remember(`${headKey(p)}:band:${detail}`, () => {
    const s = sculpt(p), head = (x: number, y: number, z: number) => s.head.field(x, y, z);
    const field = (x: number, y: number, z: number) => { const d = head(x, y, z); return Math.max(d - .0125, -(d - .004), Math.abs(y - 1.904 + .012 * smooth(-z, 0, .09)) - .011); };
    const mesh = surface(field, [-.12, 1.85, -.14], [.12, 1.96, .12], detail, HAIR);
    return [fieldPiece('leather', mesh)];
  });
}
const BEARD: Quality = { cell: .0055, reduced: { high: 1100, low: 300 } };
/** The nose's base, where the upper lip begins. */
const subnasal = (s: Sculpt) => s.tip[1] - .012;
/** Round the face from the front: the angle about an upright axis behind the face, 0 straight ahead. */
const aroundFace = (x: number, z: number) => Math.atan2(x, z + .02);
/** Positive inside the lips and just round them, negative outside: facial hair grows up to the lips' edge but not onto them. */
const lipsOf = ({ mouth }: Sculpt) => (x: number, y: number) => (1 - Math.sqrt((x / (mouth.w + .0006)) ** 2 + ((y - mouth.y + .0003) / .0071) ** 2)) * .008;
/**
 * Where each style of facial hair grows: negative inside, roughly the distance to the edge outside. Regions run
 * round the face: under a line from the nose and the corners of the mouth up to the sideburns in front of the ears,
 * over a neckline under the jaw, and clear of the lips. None comes near the eyes.
 */
function beardRegion(s: Sculpt, style: string) {
  const { mouth } = s, w = mouth.w, roof = subnasal(s) - .0016, arc = .066;
  const lips = lipsOf(s);
  /** Over the upper lip from the nose down to the lips, its ends drooping past the corners of the mouth. */
  const moustache = (x: number, y: number) => {
    const ax = Math.abs(x), top = roof - .0035 * (ax / .022) ** 2, floor = mouth.y + .0058 - .0056 * smooth(ax, w * .55, w + .004);
    // It may hang a little over the top of the upper lip.
    return Math.max(y - top, floor - y, ax - (w + .0048), lips(x, y + .0016));
  };
  /** A band round the mouth joining the moustache to the chin. */
  const ring = (x: number, y: number) => Math.max(Math.abs((Math.sqrt((x / (w + .0048)) ** 2 + ((y - mouth.y + .001) / .0138) ** 2) - 1) * .012) - .0034, y - mouth.y - .004);
  /** A rounded patch from under the lower lip to under the chin. */
  const chin = (x: number, y: number) => (Math.sqrt((x / .021) ** 2 + ((y - 1.77) / .0215) ** 2) - 1) * .017;
  const beard = (low: number) => (x: number, y: number, z: number) => {
    const angle = Math.abs(aroundFace(x, z)), back = mix(1.46, 1.29, smooth(y, 1.775, 1.815));
    const top = angle < .22 ? roof : angle < .42 ? mix(roof, 1.8045, smooth(angle, .22, .42)) : mix(1.8045, 1.853, smooth(angle, .42, 1.28) ** 1.15);
    return Math.max(y - top, mix(low, 1.766, smooth(angle, .1, 1.4)) - y, (angle - back) * arc, lips(x, y));
  };
  switch (style) {
    case 'moustache': return (x: number, y: number) => moustache(x, y);
    case 'goatee': return (x: number, y: number) => Math.max(Math.min(moustache(x, y), ring(x, y), chin(x, y)), lips(x, y));
    case 'full': return beard(1.706);
    default: return beard(1.722);
  }
}
/**
 * Facial hair as closed volumes: a shell from just under the skin to the hair's thickness, only within the style's
 * region, its edges thinning into the skin and its surface combed into strands. The part under the skin is dropped,
 * so nothing of it hides anywhere else on the face. Stubble is a shadow painted over the jaw instead.
 */
export function beardParts(p: HumanProportions, beard: string, detail: HeadDetail): HeadPiece[] {
  return remember(`${headKey(p)}:beard:${beard}:${detail}`, () => {
    const s = sculpt(p), head = (x: number, y: number, z: number) => s.head.field(x, y, z);
    if (beard === 'long' || beard === 'braided') return [...beardParts(p, 'full', detail), hangingBeard(p, s, beard === 'braided', detail)];
    if (beard === 'stubble') return [stubble(s, detail)];
    const region = beardRegion(s, beard), full = beard === 'full', { mouth } = s;
    // Strands run down the cheeks and chin in clumps, and fan out and down from the middle of the upper lip.
    const combed = (x: number, y: number, z: number) => {
      const angle = aroundFace(x, z), fan = Math.atan2(y - subnasal(s) - .004, Math.abs(x) + .003);
      const lip = smooth(y, mouth.y + .003, mouth.y + .007) * (1 - smooth(Math.abs(x), mouth.w + .002, mouth.w + .008));
      const down = .65 * Math.sin(angle * 150 + Math.sin(y * 230) * 1.2) + .35 * Math.sin(angle * 41 + y * 90);
      return mix(down, Math.sin(fan * 34 + Math.sin(x * 400)), lip);
    };
    const thickness = (x: number, y: number, z: number) => {
      const base = beard === 'moustache' ? .0072 - .0038 * smooth(Math.abs(x), .004, mouth.w + .005)
        : beard === 'goatee' ? .0036 + .0022 * (1 - smooth(y, 1.748, mouth.y - .006))
        : full ? .0058 + .0052 * (1 - smooth(y, 1.74, 1.8)) : .0034 + .0016 * (1 - smooth(y, 1.75, 1.795));
      return shell(base * (1 + .14 * combed(x, y, z)), detail);
    };
    // Facial hair thickens gradually from its edges, so they do not stand off the skin; a moustache is too narrow to. Round the mouth it
    // thickens within a few millimetres, so the hair grows right up to the lips instead of leaving a bare ring round them.
    const lips = lipsOf(s), edge = beard === 'moustache' ? .006 : beard === 'goatee' ? .005 : .01;
    const featherAt = (x: number, y: number) => mix(.0025, edge, smooth(-lips(x, y), .002, .012));
    // A full beard also hangs below the chin, narrowing to a rounded end.
    const hang = full ? roundCone([0, 1.744, .064], [0, 1.704, .07], .028, .012) : undefined;
    const field = (x: number, y: number, z: number) => {
      const d = head(x, y, z);
      let v = layer(d, thickness(x, y, z), region(x, y, z), featherAt(x, y));
      if (hang) v = Math.min(v, Math.max(hang.field(x, y, z) - .002 * (1 + .25 * combed(x, y, z)), -d - .0045));
      return v;
    };
    const mesh = surface(field, [-.095, 1.69, -.05], [.095, 1.87, .135], detail, BEARD,
      (x, y, z) => head(x, y, z) > -.0003);
    softenEdge(mesh, head, (x, y, z) => smooth(-region(x, y, z), 0, featherAt(x, y)));
    // Down the cheeks and chin; over the upper lip the moustache fans out from the middle.
    const flow = (x: number, y: number): Vec => { const lip = smooth(y, mouth.y + .003, mouth.y + .007); return [Math.sign(x) * .9 * lip, -1, .25 * (1 - lip)]; };
    const strand = { lift: HAIR_LIFT[detail], flow, axis: [0, -.02] as [number, number], cover: (x: number, y: number, z: number) => smooth(-region(x, y, z), 0, featherAt(x, y) * 1.6) };
    return mesh.positions.length ? [fieldPiece('strands', mesh, (x, y, z, out) => { const c = .76 + .17 * combed(x, y, z); out[0] = out[1] = out[2] = c; }, undefined, undefined, strand)] : [];
  });
}
/** Closed hanging hair volumes grow from the full beard and stay in front of the resolved chest. */
function hangingBeard(p: HumanProportions, s: Sculpt, braided: boolean, detail: HeadDetail): HeadPiece {
  const head = s.head.field.bind(s.head), chest = chestFront(p);
  const root = (x: number) => (front(head, x, 1.754) ?? .075) + .006;
  const shapes: Shape[] = [];
  let far = .25;
  if (braided) {
    for (const side of [-1, 1]) for (let strand = 0; strand < 3; strand++) {
      const points: Vec[] = [], radii: number[] = [];
      for (let i = 0; i <= 36; i++) {
        const t = i / 36, y = mix(1.758, 1.515, t), centre = side * mix(.023, .019, t);
        const angle = t * Math.PI * 10 + strand * Math.PI * 2 / 3, coil = .0085 * (1 - .35 * t);
        const z = Math.max(root(centre), chest(centre, y) + .05);
        far = Math.max(far, z + .04);
        points.push([centre + Math.cos(angle) * coil, y, z + Math.sin(angle) * coil]); radii.push(.0065 * (1 - .48 * t));
      }
      shapes.push(chain(points, radii, .0015));
    }
  } else {
    const points: Vec[] = [1.758, 1.718, 1.66, 1.585, 1.5].map(y => [0, y, Math.max(root(0) + .012, chest(0, y) + .075)]);
    far = Math.max(far, ...points.map(point => point[2] + .045));
    shapes.push(chain(points, [.031, .038, .034, .024, .009], .006));
  }
  const hair = union(.002, ...shapes);
  const strands = (x: number, y: number, z: number) => Math.sin(aroundFace(x, z) * 95 + y * 7) * .7 + Math.sin(x * 420 + y * 20) * .3;
  // A loose beard hangs broad and flat against the chest, not round, in a few locks; the plaits stay round.
  const middle = (y: number) => Math.max(root(0) + .012, chest(0, y) + .075);
  const hanging = braided ? hair.field.bind(hair) : (x: number, y: number, z: number) => hair.field(x * .78, y, middle(y) + (z - middle(y)) * 1.4) * .78 - .0022 * Math.max(0, Math.sin(x * 260 + Math.sin(y * 40)));
  const field = (x: number, y: number, z: number) => Math.max(hanging(x, y, z) - (braided ? .0003 : .001) * strands(x, y, z), -head(x, y, z) - .003, chest(x, y) + .004 - z);
  const mesh = surface(field, [-.09, 1.47, -.06], [.09, 1.8, far], detail, BEARD);
  return fieldPiece('strands', mesh, (x, y, z, out) => { const c = .8 + .12 * strands(x, y, z); out[0] = out[1] = out[2] = c; }, (_x, y) => 1 - smooth(y, 1.6, 1.75), hangsFrom(1.75, 1.52), { lift: HAIR_LIFT[detail], flow: () => [0, -1, 0], axis: [0, -.02] });
}
/** A repeatable pseudo-random value in [0, 1) for a grid point. */
function hash(i: number, j: number) { const h = Math.sin(i * 127.1 + j * 311.7) * 43758.5453; return h - Math.floor(h); }
/** Stubble: a shadow of short hair painted over the jaw, fading at its edges, laid round the face from the sides. */
function stubble(s: Sculpt, detail: HeadDetail): HeadPiece {
  const f = (x: number, y: number, z: number) => s.head.field(x, y, z), region = beardRegion(s, 'short'), lips = lipsOf(s), rows = 26, columns = 50;
  const points: Vec[][] = [], normals: Vec[][] = [], colors: Vec[][] = [], density: number[][] = [];
  for (let i = 0; i <= rows; i++) {
    const row: Vec[] = [], normal: Vec[] = [], color: Vec[] = [], amount: number[] = [];
    for (let j = 0; j <= columns; j++) {
      // Rays aimed at an axis inside the head reach the cheeks and the sides of the jaw as well as the chin.
      const angle = mix(-1.9, 1.9, j / columns), y = mix(1.722, 1.862, i / rows), dx = -Math.sin(angle), dz = -Math.cos(angle);
      // Keep the ray axis inside the narrower, rearward neck as well as the face. A forward axis can miss the
      // neck at the lowest rows; bounded misses stay finite and are discarded by their zero density below.
      let x = -dx * .2, z = -dz * .2 - .015, hit = false;
      for (let k = 0; k < 300 && Math.abs(x) < .3 && Math.abs(z) < .3; k++) { const d = f(x, y, z); if (d < 2e-6) { hit = true; break; } x += dx * Math.max(d * .8, 1e-5); z += dz * Math.max(d * .8, 1e-5); }
      // Short hairs as random grain, thinning out over a centimetre at the edges.
      const n = gradient(f, x, y, z), lift = .00035, dense = hit ? smooth(-region(x, y, z), 0, mix(.003, .01, smooth(-lips(x, y), .002, .012))) : 0, grain = .7 + .3 * hash(i, j);
      const c = 1 - .36 * dense * grain;
      row.push([x + n[0] * lift, y + n[1] * lift, z + n[2] * lift]); normal.push(n); color.push([c * .97, c * .96, c]); amount.push(dense);
    }
    points.push(row); normals.push(normal); colors.push(color); density.push(amount);
  }
  // Keep only the part that darkens the skin.
  const piece = gridPiece('face', points, normals, colors), kept: number[] = [];
  const shown = (index: number) => density[Math.floor(index / (columns + 1))][index % (columns + 1)] > .001;
  for (let t = 0; t < piece.indices.length; t += 3) if (shown(piece.indices[t]) || shown(piece.indices[t + 1]) || shown(piece.indices[t + 2])) kept.push(piece.indices[t], piece.indices[t + 1], piece.indices[t + 2]);
  return { ...piece, indices: new Uint32Array(kept) };
}

/** A smooth displacement of skull space, written into `out`. */
export type Warp = (x: number, y: number, z: number, out: number[]) => void;
/**
 * How a face shape moves the rig's own face: local moves, swellings and scalings around the features, each by how
 * far the shape's measure differs from the rig's own (`HumanFace`). The skin, eyes, painted details and facial hair
 * all move with it, so every face shape keeps the same surfaces. A shared medial orbital inset also applies to
 * the rig's own face, keeping the inner eye opening behind the nasal sidewall in profile.
 */
export function faceWarp(p: HumanProportions, face: HumanFace, extras?: Partial<FaceWarpExtras>): Warp {
  const { eye, mouth, noseBase } = sculpt(p);
  const spacing = extras?.eyeSpacing ?? 0, sag = extras?.sag ?? 0;
  // The fold from the nose wing to past the mouth corner, which sag deepens: its ends, as in the sculpt's own `fold`.
  const wing = [.017 * face.nose, noseBase], corner = [mouth.w + .006, mouth.y - .008], lx = corner[0] - wing[0], ly = corner[1] - wing[1], l2 = lx * lx + ly * ly;
  // Most numbers are sculpted into the rig's own skull, so a face shape moves only what it adds to them. Width, length, nose size and eye depth are
  // not in the skull at all: those are measured against the neutral face, so the rig's own values shape its face too.
  const base: HumanFace = { ...p.face, ...(p.faceNeutral ?? HUMAN_FACE_NEUTRAL[p.motionRig]) };
  const by = (key: keyof HumanFace) => face[key] - base[key], scaled = (key: keyof HumanFace) => base[key] ? face[key] / base[key] - 1 : 0;
  const t = { width: scaled('width'), length: scaled('length'), noseSize: scaled('noseSize'), jaw: scaled('jaw'), chin: scaled('chin'), chinDepth: by('chinDepth'), square: by('square'), brow: by('brow'), cheek: by('cheek'), hollow: by('hollow'),
    eye: by('eye'), eyeSize: scaled('eyeSize'), eyeTilt: by('eyeTilt'), nose: scaled('nose'), noseLength: by('noseLength'), bridge: by('bridge'), lips: scaled('lips'), fullness: by('fullness') };
  const g = (dx: number, dy: number, dz: number, rx: number, ry: number, rz: number) => Math.exp(-(dx * dx) / (rx * rx) - (dy * dy) / (ry * ry) - (dz * dz) / (rz * rz));
  return (x, y, z, out) => {
    const ax = Math.abs(x), side = x < 0 ? -1 : 1;
    let dx = 0, dy = 0, dz = 0;
    // Recess only the nasal corner and its surrounding lid. The iris centre and outer corner retain their depth,
    // and moving skin, aperture rims, eyeball and painted details together keeps the opening sealed.
    const inner = 1 - smooth((ax - eye.x) / eye.w, -.9, -.12);
    dz -= .0035 * inner * smooth(ax, .009, .013) * gauss(y - eye.y, .017) * smooth(z, .045, .067);
    // The face below the brow widens or narrows; the skull, the ears, the temples and the sideburns stay where the hair
    // fits them, since hair is not shaped per face.
    if (t.width) dx += x * t.width * smooth(y, 1.73, 1.765) * (1 - smooth(y, 1.83, 1.86)) * smooth(z, -.035, .005) * (1 - smooth(ax, .05, .066) * smooth(y, 1.8, 1.83));
    // The face below the eyes lengthens or shortens toward the chin; the neck, which meets the body's, stays.
    if (t.length) dy += (y - 1.858) * t.length * (1 - smooth(y, 1.85, 1.875)) * smooth(y, 1.7, 1.758) * smooth(z, -.04, 0);
    if (t.noseSize) {
      // The whole nose scales about its root; the cheeks and lip round it give way.
      const r = Math.sqrt((x / .028) ** 2 + ((y - 1.838) / .042) ** 2 + ((z - .1) / .03) ** 2), w = 1 - smooth(r, .75, 1.35);
      dx += x * t.noseSize * w; dy += (y - 1.866) * t.noseSize * w; dz += (z - .084) * t.noseSize * w;
    }
    // The lower face widens or narrows; nothing moves above the cheeks or down the neck, which meets the body's.
    if (t.jaw) dx += x * t.jaw * (1 - smooth(y, 1.795, 1.835)) * smooth(y, 1.73, 1.765) * smooth(z, -.045, -.015);
    if (t.chin) dx += x * t.chin * g(x, y - 1.767, z - .083, .035, .024, .035);
    if (t.chinDepth) { const w = g(x, y - 1.767, z - .083, .027, .023, .035); dz += t.chinDepth * w; dy -= t.chinDepth * .2 * w; }
    if (t.square) { const w = t.square * g(ax - .05, y - 1.783, z, .026, .024, .035); dx += side * .005 * w; dy -= .002 * w; }
    if (t.brow) dz += t.brow * .8 * Math.exp(-(((y - 1.8798 + .0016 * (ax / .05) ** 2) / .0074) ** 2)) * (1 - smooth(ax, .044, .064)) * smooth(z, .03, .07);
    if (t.cheek) { const w = .2 * t.cheek * g(ax - .051, y - 1.838, z - .055, .025, .022, .03); dx += side * .6 * w; dz += .6 * w; }
    if (t.hollow) { const w = t.hollow * g(ax - .057, y - 1.802, z - .045, .025, .024, .032); dx -= side * .25 * w; dz -= .25 * w; }
    if (t.fullness) { const w = .45 * t.fullness * g(ax - .042, y - 1.823, z - .06, .026, .025, .03); dx += side * .6 * w; dz += .5 * w; }
    if (t.eye || t.eyeSize || t.eyeTilt) {
      // The eye and its lids, scaled about the front of the eye, set deeper, and turned so the outer corner lifts.
      const ux = ax - eye.x, vy = y - eye.y, uz = z - (eye.z + eye.r), w = 1 - smooth(Math.sqrt(ux * ux + vy * vy + uz * uz), .014, .036);
      if (w > 0) {
        dx += side * (ux * t.eyeSize - vy * t.eyeTilt) * w; dy += (vy * t.eyeSize + ux * t.eyeTilt) * w; dz += (uz * t.eyeSize - t.eye * .45) * w;
      }
    }
    if (t.nose) dx += x * t.nose * g(x, y - 1.826, z - .1, .025, .03, .025);
    if (t.noseLength) { const w = smooth(1.866 - y, 0, .045) * smooth(y, 1.797, 1.813) * g(x, 0, z - .1, .02, 1, .025); dy -= .0435 * t.noseLength * w; dz += .004 * t.noseLength * w; }
    if (t.bridge) dz += t.bridge * g(x, y - 1.848, z - .095, .007, .012, .012);
    if (t.lips) { const w = g(x, y - mouth.y, z - .09, .03, .013, .016); dy += (y - mouth.y) * t.lips * w; dz += (z - .09 + .004) * t.lips * .5 * w; }
    if (spacing) {
      // The eyes and their lids move apart or together as one, the bridge of the nose between them staying where it is.
      const ux = ax - eye.x, vy = y - eye.y, uz = z - (eye.z + eye.r), w = (1 - smooth(Math.sqrt(ux * ux + vy * vy + uz * uz), .016, .032)) * smooth(ax, .006, .016);
      dx += side * .0028 * spacing * w;
    }
    if (sag && z > -.02) {
      // Age lets the soft tissue fall: the cheeks slide down onto the fold, the jowls gather below the mouth corners, the corners themselves
      // turn down, bags fill under the eyes and the outer brow droops. The fold from the nose wing deepens with the cheek rounding over it.
      const front = smooth(z, .02, .05);
      const cheek = g(ax - .041, y - 1.824, z - .066, .017, .016, .026), jowl = g(ax - .044, y - 1.771, z - .055, .016, .012, .03);
      dy -= sag * (.004 * cheek + .006 * jowl) * front; dx += side * sag * .0022 * jowl * front; dz += sag * .0008 * cheek * front;
      const mouthCorner = g(ax - mouth.w * 1.05, y - mouth.y, z - .085, .005, .005, .02);
      dy -= sag * .0016 * mouthCorner;
      const bag = Math.exp(-(((ax - eye.x - .002) / .01) ** 2) - ((y - (eye.y - eye.down - .0065)) / .0034) ** 2) * smooth(z, .055, .07);
      dz += sag * .002 * bag;
      const brow = g(ax - eye.x - .011, y - eye.y - .016, z - .07, .011, .006, .03);
      dy -= sag * .0022 * brow;
      const u = clamp(((ax - wing[0]) * lx + (y - wing[1]) * ly) / l2, 0, 1), across = ((ax - wing[0]) * ly - (y - wing[1]) * lx) / Math.sqrt(l2);
      const along = Math.hypot(ax - wing[0] - u * lx, y - wing[1] - u * ly) * Math.sign(across || 1), fade = Math.sin(Math.PI * u) ** .6 * front;
      dz += sag * fade * (-.0016 * gauss(along, .0026) + .001 * gauss(along + .0055, .0055));
    }
    out[0] = dx; out[1] = dy; out[2] = dz;
  };
}
